/**
 * NOURION Konfigurator Oberfläche: Custom Element <nrn-konfigurator> (Masterplan 4.4, Aufgabe B5).
 *
 * Fachlogik: Spezifikation 03 Abschnitte 2 (Ablauf), 4 (Eingabe, Zustand, Barrierefreiheit, Mobil),
 * 5 (Warenkorb) und 6.4 bis 6.6 (Link, Nummer, Anfrage). Rechnen, Prüfen und Serialisieren erledigt
 * ausschließlich nrn-konfigurator-logik.js; dieses Modul baut die Oberfläche und verbindet Ereignisse.
 *
 * Grundsätze:
 *   * Das Element liest vier JSON Blöcke (regeln, texte, katalog, einstellungen) und baut das Formular
 *     in [data-nrn-app]. connectedCallback ist idempotent (Theme Editor, shopify:section:load).
 *   * Alle sichtbaren Texte kommen aus der Textdatei (L.text), der Regeldatei oder den Einstellungen.
 *     Im Code stehen keine sichtbaren Sätze. Klassenlisten werden als Arrays gebaut.
 *   * Die Oberfläche hält den letzten gültigen Zustand. Ungültige Eingaben bleiben sichtbar stehen,
 *     werden markiert und sperren den Warenkorb; nichts wird still korrigiert.
 *   * Keine Netzwerkaufrufe im Theme Editor bzw. in der visuellen Vorschau.
 *   * Geld nur über L.formatCent (Intl de-DE EUR), Zahlen über L.formatZahl.
 */
import * as L from '@theme/nrn-konfigurator-logik';
import {
  addLinesToCart,
  replaceConfiguration,
  removeConfiguration,
  fetchCart,
  closeCartDrawer,
  routen,
  NrnCartError,
} from '@theme/nrn-cart';

/* ------------------------------------------------------------------------------------------------
 * Konstanten
 * --------------------------------------------------------------------------------------------- */

const SPEICHER_LETZTE = 'nrn-konfigurator-letzte';
const SPEICHER_ANFRAGE = 'nrn-anfrage';
const HASH_VERZOEGERUNG = 300;
const ANSAGE_VERZOEGERUNG = 750;
const RUECKGAENGIG_DAUER = 10000;
const STATUS_DAUER = 6000;
const PAKETE = Object.freeze(['basis', 'workplace', 'enterprise']);
const STUFE = Object.freeze({ basis: 1, workplace: 2, enterprise: 3 });
/** Ersatz, falls die Einstellung paketNamen fehlt (Produktnamen, Masterplan E9). */
const PAKET_NAMEN = Object.freeze({ basis: 'Basis', workplace: 'Workplace', enterprise: 'Enterprise' });
const MARKE = 'NOURION';
const STANDARD_KONTAKT = '/pages/contact';
const MQ_DESKTOP = '(min-width:990px)';
// Regler: die Eingabegrenze (bis 1.000 Geraete, bis 100.000 GB) bleibt im Feld, der Schieberegler deckt nur den
// Alltagsbereich ab, sonst klebt der Knopf bei 5 bis 50 links und ein Pixel sind mehrere Einheiten (Sichtpruefung 08.10.2026).
// Ueber 1.000 (Speicherfelder bis 100.000) verschwindet der Regler ganz, Feld und Stepper bleiben.
const REGLER_MAX = 250;
const REGLER_AUS_AB = 1000;
const reglerMax = (max) => Math.min(max, REGLER_MAX);

/**
 * Füllung des Reglers in Prozent (--slider-progress). Gerechnet gegen die Reglergrenze, nicht gegen die
 * Feldgrenze, sonst endet die Füllung bei den 1.000er Feldern weit vor dem Daumen; Werte über der
 * Reglergrenze füllen ganz, passend zum Daumen am Ende (Sichtpruefung 12.10.2026, einstieg-2 und feintuning-1).
 * @param {number} wert
 * @param {number} min
 * @param {number} max  Feldgrenze
 * @returns {number}
 */
function reglerFortschritt(wert, min, max) {
  const rmx = reglerMax(Math.max(min, max));
  if (!(rmx > min)) return 0;
  const v = Math.min(Math.max(wert, min), rmx);
  return ((v - min) / (rmx - min)) * 100;
}
const MQ_RUHIG = '(prefers-reduced-motion:reduce)';
/** Hauptzeiger ist ein Finger (Handy, Tablet): dort öffnet Link teilen das Teilen Fenster. */
const MQ_TOUCH = '(pointer:coarse)';
const SVG_NS = 'http://www.w3.org/2000/svg';
const ZAHL_ZWEI = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const DATUM = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

/** Symbole (20 × 20, Strich), immer dekorativ. */
const SYMBOLE = Object.freeze({
  minus: 'M4.5 10h11',
  plus: 'M10 4.5v11M4.5 10h11',
  chevron: 'M5 7.5L10 12.5L15 7.5',
  haken: 'M4.5 10.5L8 14L15.5 6',
  schliessen: 'M5.5 5.5L14.5 14.5M14.5 5.5L5.5 14.5',
  achtung: 'M10 3.2l7.6 13.3H2.4zM10 8.2v3.8M10 14.3v.2',
  schloss: 'M5.5 9H14.5V16.5H5.5ZM7.2 9V6.8A2.8 2.8 0 0 1 12.8 6.8V9',
});

/* ------------------------------------------------------------------------------------------------
 * Kleine DOM Helfer (ohne Abhängigkeiten)
 * --------------------------------------------------------------------------------------------- */

/**
 * Erzeugt ein Element. attrs: class (Text oder Liste), text, sonst Attribute (true = leeres Attribut,
 * false, null, undefined = weglassen). Kinder: Knoten, Texte oder (verschachtelte) Listen.
 * @param {string} tag
 * @param {Record<string, unknown>|null} [attrs]
 * @param {...unknown} kinder
 * @returns {HTMLElement}
 */
function h(tag, attrs, ...kinder) {
  const el = document.createElement(tag);
  if (attrs) {
    for (const [name, wert] of Object.entries(attrs)) {
      if (wert === null || wert === undefined || wert === false) continue;
      if (name === 'class') el.className = Array.isArray(wert) ? wert.filter(Boolean).join(' ') : String(wert);
      else if (name === 'text') el.textContent = String(wert);
      else el.setAttribute(name, wert === true ? '' : String(wert));
    }
  }
  anhaengen(el, kinder);
  return el;
}

function anhaengen(el, kinder) {
  for (const k of kinder) {
    if (k === null || k === undefined || k === false) continue;
    if (Array.isArray(k)) anhaengen(el, k);
    else if (typeof k === 'object' && typeof k.nodeType === 'number') el.append(k);
    else el.append(String(k));
  }
}

/** Ersetzt die Kinder; wie h() werden Listen eingeebnet und null, undefined, false übergangen. */
function fuelle(el, ...kinder) {
  el.replaceChildren();
  anhaengen(el, kinder);
}

/**
 * Dekoratives SVG Symbol.
 * @param {keyof typeof SYMBOLE} name
 * @param {string|string[]} [klasse]
 */
function symbol(name, klasse) {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 20 20');
  svg.setAttribute('width', '20');
  svg.setAttribute('height', '20');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  svg.setAttribute('class', ['nrn-konf__symbol'].concat(klasse ?? []).join(' '));
  const pfad = document.createElementNS(SVG_NS, 'path');
  pfad.setAttribute('d', SYMBOLE[name]);
  pfad.setAttribute('fill', 'none');
  pfad.setAttribute('stroke', 'currentColor');
  pfad.setAttribute('stroke-width', '1.8');
  pfad.setAttribute('stroke-linecap', 'round');
  pfad.setAttribute('stroke-linejoin', 'round');
  svg.append(pfad);
  return svg;
}

/** Stufenabzeichen (02 Abschnitt 2.5): drei Ebenen, n davon gefüllt. */
function stufenSymbol() {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('class', 'k-tier__icon');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  [[1, 11], [2, 6.5], [3, 2]].forEach(([nr, y]) => {
    const r = document.createElementNS(SVG_NS, 'rect');
    r.setAttribute('class', ['k-tier__bar', `k-tier__bar--${nr}`].join(' '));
    r.setAttribute('x', '2');
    r.setAttribute('y', String(y));
    r.setAttribute('width', '12');
    r.setAttribute('height', '3');
    r.setAttribute('rx', '1');
    svg.append(r);
  });
  return svg;
}

/** Setzt textContent nur bei Änderung (vermeidet unnötige Ansagen und Layoutarbeit). */
function setzeText(el, wert) {
  const s = wert === null || wert === undefined ? '' : String(wert);
  if (el.textContent !== s) el.textContent = s;
}

function setzeVerborgen(el, verborgen) {
  if (el.hidden !== Boolean(verborgen)) el.hidden = Boolean(verborgen);
}

function setzeAttr(el, name, wert) {
  if (wert === null || wert === undefined || wert === false) {
    if (el.hasAttribute(name)) el.removeAttribute(name);
  } else {
    const s = wert === true ? '' : String(wert);
    if (el.getAttribute(name) !== s) el.setAttribute(name, s);
  }
}

/**
 * Sperrt ein Bedienelement, ohne ihm den Fokus zu nehmen: aria-disabled, sobald es nichts bewirken darf;
 * disabled zusätzlich nur, wenn das ganze Feld gesperrt ist und das Element gerade nicht fokussiert ist.
 */
function setzeSperre(el, aus, alles, aktiv) {
  setzeAttr(el, 'aria-disabled', aus ? 'true' : null);
  const ab = Boolean(alles) && aktiv !== el;
  if (el.disabled !== ab) el.disabled = ab;
}

function istGesperrt(el) {
  return el?.getAttribute('aria-disabled') === 'true' || Boolean(el?.disabled);
}

function kopiereZustand(z) {
  return {
    ...z,
    einstieg: z.einstieg ? { ...z.einstieg } : null,
    produkte: { ...z.produkte },
    schalter: { ...z.schalter },
    felder: { ...z.felder },
  };
}

function ruhig() {
  try {
    return Boolean(window.matchMedia?.(MQ_RUHIG)?.matches);
  } catch {
    return false;
  }
}

/** Führt fn vor dem nächsten Bild aus (Layout und Scrollhöhe stehen), ohne requestAnimationFrame sofort. */
function naechstesBild(fn) {
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(fn);
  else fn();
}

function istTouchGeraet() {
  try {
    return Boolean(window.matchMedia?.(MQ_TOUCH)?.matches);
  } catch {
    return false;
  }
}

function speicherLesen(speicher, schluessel) {
  try {
    return window[speicher]?.getItem(schluessel) ?? null;
  } catch {
    return null;
  }
}

function speicherSchreiben(speicher, schluessel, wert) {
  try {
    if (!window[speicher]) return false;
    if (wert === null) window[speicher].removeItem(schluessel);
    else window[speicher].setItem(schluessel, wert);
    return true;
  } catch {
    return false;
  }
}

/** Nur eigene Pfade oder http(s) Adressen zulassen (Einstellungen kommen aus dem Editor). */
function sichereUrl(url, pfadErlaubt) {
  if (typeof url !== 'string') return '';
  const s = url.trim();
  if (pfadErlaubt && /^\/(?!\/)/.test(s)) return s;
  return /^https?:\/\//i.test(s) ? s : '';
}

/**
 * Liegt die Adresse (aus sichereUrl: eigener Pfad oder http(s)) auf einer anderen Herkunft als diese Seite?
 * @param {string} url
 * @returns {boolean}
 */
function fremdeHerkunft(url) {
  if (!/^https?:\/\//i.test(url)) return false;
  try {
    return new URL(url).origin !== window.location.origin;
  } catch {
    return true;
  }
}

/**
 * Hängt ?anfrage= (bzw. &anfrage=) an, vor einem Sprungziel #…, damit der Parameter in der Abfrage landet.
 * @param {string} url
 * @param {string} text
 * @returns {string}
 */
function mitAnfrage(url, text) {
  const i = url.indexOf('#');
  const basis = i < 0 ? url : url.slice(0, i);
  const ziel = i < 0 ? '' : url.slice(i);
  return `${basis}${basis.includes('?') ? '&' : '?'}anfrage=${encodeURIComponent(text)}${ziel}`;
}

/* ------------------------------------------------------------------------------------------------
 * Custom Element
 * --------------------------------------------------------------------------------------------- */

class NrnKonfigurator extends HTMLElement {
  /* Daten */
  #R = null;
  #texte = null;
  #ks = null;
  #einst = null;
  #designModus = false;
  #uid = 'nrn';

  /* Zustand */
  #z = null;
  #b = null;
  #antworten = {};
  #fehler = new Map();
  #einstiegFehler = new Map();
  #feldHinweise = new Map();
  #offen = new Set();
  #paketeSichtbar = false;
  #vorgewaehlt = null;
  #uebernommen = null;
  /** Antworten beim Übernehmen des Pakets (Schlüssel aus #antwortSchluessel), für den Hinweis nach Änderungen. */
  #uebernommenAntworten = null;
  #hinweise = [];
  #hinweisVerwerfen = false;
  #detailsOffen = false;
  #beschaeftigt = false;
  #rueckgaengig = null;
  #offeneAlte = [];
  /** Nummer, die beim Ersetzen in den Warenkorb kam, während #offeneAlte noch dort liegen (Befund 20). */
  #offeneNummer = null;
  #startModul = null;
  /** Modul aus #modul= beim Start mit wiederhergestellter Konfiguration; Verwerfen behält es (Befund 7). */
  #linkModul = null;

  /* Zwischenspeicher */
  #paketCache = { schluessel: '', preise: null, empfohlen: null };
  #druckSchluessel = '';
  #letzterHash = null;
  #letzteNetto = null;
  #angesagt = null;
  #titelVorDruck = null;

  /* Laufzeit */
  #gebaut = false;
  #verbunden = false;
  #timer = { hash: 0, ansage: 0, status: 0, fokus: 0 };
  #el = {};
  #mq = null;

  #h = {
    eingabe: (e) => this.#beiEingabe(e),
    aenderung: (e) => this.#beiAenderung(e),
    klick: (e) => this.#beiKlick(e),
    taste: (e) => this.#beiTaste(e),
    hash: () => this.#beiHashwechsel(),
    seite: (e) => this.#beiPageshow(e),
    vorDruck: () => this.#vorDruck(),
    nachDruck: () => this.#nachDruck(),
    breite: () => this.#beiBreite(),
  };

  connectedCallback() {
    if (this.#gebaut) {
      this.#verbindeGlobal();
      return;
    }
    try {
      if (!this.#lies()) return;
      this.#baue();
      this.#gebaut = true;
      this.#verbindeGlobal();
      this.#aktualisiere();
      this.#nachStart();
      this.setAttribute('data-nrn-bereit', 'true');
    } catch (fehler) {
      console.error('[nrn konfigurator] init failed', fehler);
    }
  }

  disconnectedCallback() {
    this.#trenneGlobal();
  }

  /* ----------------------------------------------------------------------------------------------
   * Start
   * ------------------------------------------------------------------------------------------- */

  /** Liest die vier JSON Blöcke. Liefert false, wenn der Konfigurator nicht starten kann. */
  #lies() {
    const daten = (name) => {
      const s = this.querySelector(`script[type="application/json"][data-nrn="${name}"]`);
      if (!s) return null;
      try {
        return JSON.parse(s.textContent || 'null');
      } catch (fehler) {
        console.error(`[nrn konfigurator] JSON block ${name} invalid`, fehler);
        return null;
      }
    };
    this.#texte = daten('texte') ?? { schema: 1, texte: {} };
    const einst = daten('einstellungen');
    this.#einst = L.normalisiereEinstellungen(einst);
    this.#einst.kontaktPfad = sichereUrl(this.#einst.kontaktPfad, true) || STANDARD_KONTAKT;
    this.#einst.terminUrl = sichereUrl(this.#einst.terminUrl, true);
    for (const k of ['einrichtungText', 'laufzeitText', 'fusszeile', 'seitenUrl']) {
      this.#einst[k] = typeof this.#einst[k] === 'string' ? this.#einst[k].trim() : '';
    }
    const shop = typeof window !== 'undefined' ? window.Shopify : undefined;
    this.#designModus = this.#einst.designModus === true || Boolean(shop?.designMode) || Boolean(shop?.visualPreviewMode);
    const abschnitt = String(this.dataset.sectionId ?? '').replace(/[^A-Za-z0-9]/g, '');
    this.#uid = 'nrn' + abschnitt.slice(Math.max(0, abschnitt.length - 12));

    const regeln = L.ladeRegeln(daten('regeln'));
    if (!regeln.ok) {
      console.error('[nrn konfigurator] rules invalid', regeln.fehler);
      this.#zeigeNotfall();
      return false;
    }
    this.#R = regeln.regeln;
    this.#ks = L.pruefeKatalog(this.#R, daten('katalog'), this.#einst);
    if (this.#ks.warnungen.length) console.warn('[nrn konfigurator] catalog', this.#ks.warnungen);
    return true;
  }

  /** Ohne gültige Regeln: nur der Kontaktweg. */
  #zeigeNotfall() {
    const app = this.querySelector('[data-nrn-app]');
    if (!app) return;
    app.replaceChildren(
      h('p', { class: ['k-hint', 'nrn-konf__notfall'] },
        this.#t('sperre.nurAngebot'), ' ',
        h('a', { class: 'k-link', href: this.#einst?.kontaktPfad || STANDARD_KONTAKT }, this.#t('aktion.angebot')))
    );
  }

  #t(key, params) {
    return L.text(this.#texte, key, params);
  }

  #id(teil) {
    const sauber = String(teil).replace(/[^A-Za-z0-9_]/g, '_');
    return `${this.#uid}_${sauber}`;
  }

  #standardAntworten() {
    const a = {};
    for (const f of this.#R.einstieg) a[f.id] = f.standard;
    return a;
  }

  #baue() {
    this.#z = L.standardZustand(this.#R);
    this.#antworten = this.#standardAntworten();
    this.#ladeStart();

    let app = this.querySelector('[data-nrn-app]');
    if (!app) {
      app = h('div', { class: 'nrn-konf__app', 'data-nrn-app': '' });
      this.append(app);
    }
    this.#el = {
      einstieg: {}, felder: {}, positionen: {}, produkte: {}, schalter: {}, pakete: {}, dialoge: {}, summe: {},
    };
    const haupt = h('div', { class: 'nrn-konf__haupt' },
      this.#baueHinweise(), this.#baueEinstieg(), this.#bauePakete(), this.#baueFeintuning());
    const rand = h('aside', { class: 'nrn-konf__rand', 'aria-label': this.#t('summe.titel') },
      h('div', { class: 'nrn-konf__klebe' }, this.#baueSumme(), this.#baueAbschluss()));
    this.#el.ansage = h('div', { class: 'k-sr-only', 'aria-live': 'polite', 'aria-atomic': 'true' });
    app.replaceChildren(
      h('div', { class: 'nrn-konf__layout' }, haupt, rand),
      this.#baueDialog('gleich'), this.#baueDialog('ersetzen'), this.#baueDialog('zuruecksetzen'),
      this.#el.ansage
    );
    this.#el.druck = h('div', { class: 'nrn-druck', 'data-nrn-druck': '' });

    this.addEventListener('input', this.#h.eingabe);
    this.addEventListener('change', this.#h.aenderung);
    this.addEventListener('click', this.#h.klick);
    this.addEventListener('keydown', this.#h.taste);
  }

  /**
   * Startzustand: Hash, sonst letzte Konfiguration aus localStorage, sonst Einstieg mit Standardwerten.
   * Nur Hashes, die wie ein Konfigurator Link aussehen, zählen (L.istKonfigLink); #MainContent aus dem Link
   * "Zum Inhalt springen" ergab sonst "Der Link konnte nicht gelesen werden" (Befund 4). #modul= ergänzt wie beim
   * Hashwechsel den Zustand, hier also die wiederhergestellte Konfiguration samt Hinweis und Verwerfen; vorher
   * blieb nur das Modul bei 0,00 € und der Hash überschrieb die gespeicherte Konfiguration still (Befund 7).
   */
  #ladeStart() {
    const roh = this.#hashText();
    let modul = null;
    if (roh && L.istKonfigLink(roh)) {
      const r = L.deserialisiere(roh, this.#R);
      if (r.kurzform?.modul) modul = r;
      else if (r.zustand || r.kurzform) {
        this.#wendeLinkAn(r, roh, true);
        return;
      } else this.#hinweise = r.hinweise;
    }
    if (!this.#designModus) this.#stelleLetzteHer();
    if (modul) {
      this.#wendeLinkAn(modul, roh, true);
      // Verwerfen gilt der wiederhergestellten Konfiguration, das Modul aus dem Link bleibt dann einbezogen.
      if (this.#hinweisVerwerfen) this.#linkModul = modul.kurzform.modul;
    }
  }

  /** Letzte Konfiguration aus localStorage mit Hinweis "wiederhergestellt" und Verwerfen. */
  #stelleLetzteHer() {
    const gespeichert = speicherLesen('localStorage', SPEICHER_LETZTE);
    if (!gespeichert) return;
    const r = L.deserialisiere(gespeichert, this.#R);
    if (!r.zustand) return;
    this.#uebernimmZustand(r.zustand);
    this.#hinweise = [...this.#hinweise, { code: 'wiederhergestellt' }];
    this.#hinweisVerwerfen = true;
  }

  #hashText() {
    try {
      return String(window.location.hash || '').replace(/^#/, '');
    } catch {
      return '';
    }
  }

  /** Übernimmt einen vollständigen Zustand (aus Link oder Speicher) samt abgeleiteter Oberfläche. */
  #uebernimmZustand(z) {
    this.#z = z;
    // Ohne Einstieg gibt es keine Paketkarten, also auch kein Paket (wie #selbst). Mit Einstieg prüft #pruefePaket den
    // Paketnamen aus dem Link gegen die Mengen: weichen sie ab, heißt es "Individuell" (03 Abschnitt 2.3, Kandidat 74).
    if (!z.einstieg) z.paket = null;
    this.#antworten = z.einstieg ? { ...z.einstieg } : this.#standardAntworten();
    this.#paketeSichtbar = Boolean(z.einstieg);
    this.#uebernommen = PAKETE.includes(z.paket) ? z.paket : null;
    this.#uebernommenAntworten = this.#uebernommen ? this.#antwortSchluessel(z.einstieg) : null;
    this.#pruefePaket();
    this.#fehler.clear();
    this.#einstiegFehler.clear();
    this.#feldHinweise.clear();
    const erstes = this.#R.produktReihenfolge.find((id) => z.produkte[id]);
    this.#offen = new Set(erstes ? [erstes] : []);
  }

  /**
   * Wendet das Ergebnis von deserialisiere an. Kurzformen: #paket= merkt das Paket vor (#nachStart bzw.
   * #beiHashwechsel übernehmen es sofort), #modul= aktiviert das Produkt und öffnet seinen Abschnitt.
   */
  #wendeLinkAn(r, roh, start) {
    if (r.zustand) {
      this.#uebernimmZustand(r.zustand);
      this.#hinweise = r.hinweise;
      this.#hinweisVerwerfen = false;
      this.#linkModul = null;
      // Ein vollständiger Link ersetzt auch ein vorgemerktes Paket aus #paket= (Befund 21).
      this.#vorgewaehlt = null;
      this.#letzterHash = roh;
      return 'zustand';
    }
    if (r.kurzform?.paket) {
      this.#letzterHash = roh;
      this.#vorgewaehlt = r.kurzform.paket;
      return 'paket';
    }
    if (r.kurzform?.modul) {
      const id = r.kurzform.modul;
      const z = kopiereZustand(this.#z);
      z.produkte[id] = true;
      this.#z = z;
      this.#offen.add(id);
      this.#startModul = id;
      this.#letzterHash = roh;
      // Auch beim Start: die wiederhergestellte Konfiguration kann ein Paket tragen (Befund 7).
      this.#pruefePaket();
      return 'modul';
    }
    this.#hinweise = r.hinweise;
    return null;
  }

  /**
   * Kurzformen nach dem ersten Aufbau. #paket= übernimmt das Paket gleich beim Laden wie der Knopf „Paket
   * übernehmen“ (Einstieg mit Standardantworten, gleiche Mengen, Statusmeldung, Sprung zum Feintuning); vorher
   * stand man bei 0,00 € und erst „Empfehlung anzeigen“ übernahm es (QA 15.10.2026). #modul= springt zu den
   * Mengenfeldern des Moduls, Mengen bleiben 0.
   */
  #nachStart() {
    if (this.#vorgewaehlt) this.#uebernehmeVorwahl(false, true);
    if (this.#startModul) {
      const id = this.#startModul;
      this.#startModul = null;
      this.#planeHash();
      naechstesBild(() => this.#zeigeModulFelder(id));
    }
  }

  /* ----------------------------------------------------------------------------------------------
   * Globale Listener
   * ------------------------------------------------------------------------------------------- */

  #verbindeGlobal() {
    if (this.#verbunden || !this.#gebaut) return;
    this.#verbunden = true;
    window.addEventListener('hashchange', this.#h.hash);
    window.addEventListener('pageshow', this.#h.seite);
    window.addEventListener('beforeprint', this.#h.vorDruck);
    window.addEventListener('afterprint', this.#h.nachDruck);
    try {
      this.#mq = window.matchMedia?.(MQ_DESKTOP) ?? null;
      this.#mq?.addEventListener?.('change', this.#h.breite);
    } catch {
      this.#mq = null;
    }
    document.body?.classList.add('has-k-sum');
    if (this.#el.druck && !this.#el.druck.isConnected) document.body?.append(this.#el.druck);
  }

  #trenneGlobal() {
    if (!this.#verbunden) return;
    this.#verbunden = false;
    window.removeEventListener('hashchange', this.#h.hash);
    window.removeEventListener('pageshow', this.#h.seite);
    window.removeEventListener('beforeprint', this.#h.vorDruck);
    window.removeEventListener('afterprint', this.#h.nachDruck);
    this.#mq?.removeEventListener?.('change', this.#h.breite);
    for (const k of Object.keys(this.#timer)) {
      clearTimeout(this.#timer[k]);
      this.#timer[k] = 0;
    }
    if (this.#rueckgaengig?.timer) clearTimeout(this.#rueckgaengig.timer);
    this.#el.druck?.remove();
    // Dieses Element ist nicht mehr im Dokument; nur andere Konfiguratoren halten die Klasse.
    if (!document.querySelector('nrn-konfigurator[data-nrn-bereit]')) document.body?.classList.remove('has-k-sum');
  }

  /* ----------------------------------------------------------------------------------------------
   * Aufbau: Hinweise, Einstieg, Pakete
   * ------------------------------------------------------------------------------------------- */

  #baueHinweise() {
    const liste = h('ul', { class: 'nrn-konf__hinweisliste' });
    const verwerfen = h('button', {
      type: 'button', class: ['k-btn', 'k-btn--outline', 'k-btn--sm'], 'data-nrn-aktion': 'verwerfen', hidden: true,
    }, this.#t('aktion.verwerfen'));
    const bereich = h('div', {
      class: ['k-hint', 'k-hint--info', 'nrn-konf__hinweise'], role: 'status', 'data-nrn-hinweise': '', hidden: true,
    },
    h('div', { class: 'nrn-konf__hinweise-kopf' },
      h('strong', { class: 'nrn-konf__hinweise-titel' }, this.#t('hinweise.titel')),
      h('button', {
        type: 'button', class: 'nrn-konf__schliessen', 'aria-label': this.#t('hinweise.schliessen'),
        'data-nrn-hinweise-schliessen': '',
      }, symbol('schliessen'))),
    liste, verwerfen);
    this.#el.hinweise = { bereich, liste, verwerfen };
    return bereich;
  }

  #baueEinstieg() {
    const titelId = this.#id('einstieg_titel');
    const fragen = this.#R.einstieg.map((f) => this.#baueFrage(f));
    this.#el.grenze = h('p', { class: ['k-hint', 'nrn-konf__grenze'], hidden: true },
      this.#t('einstieg.grenze'), ' ',
      h('button', { type: 'button', class: ['k-link', 'nrn-konf__textknopf'], 'data-nrn-grenze-angebot': '' },
        this.#t('einstieg.grenzeLink')));
    return h('section', { class: ['k-card', 'nrn-konf__einstieg'], 'aria-labelledby': titelId },
      h('h2', { id: titelId, class: 'nrn-konf__h2' }, this.#t('einstieg.titel')),
      h('p', { class: 'nrn-konf__text' }, this.#t('einstieg.text')),
      h('div', { class: 'nrn-konf__fragen' }, fragen),
      this.#el.grenze,
      h('div', { class: 'nrn-konf__aktionen' },
        h('button', { type: 'button', class: ['k-btn', 'k-btn--rot'], 'data-nrn-aktion': 'empfehlung' },
          this.#t('einstieg.empfehlung')),
        h('button', { type: 'button', class: ['k-btn', 'k-btn--outline'], 'data-nrn-aktion': 'selbst' },
          this.#t('einstieg.selbst'))));
  }

  #baueFrage(f) {
    const feldId = this.#id(`e_${f.id}`);
    const labelId = `${feldId}_l`;
    const hilfeId = `${feldId}_h`;
    const fehlerId = `${feldId}_f`;
    const regler = h('input', {
      type: 'range', class: ['k-slider', 'nrn-konf__regler'], min: f.min, max: reglerMax(f.max), step: 1,
      value: this.#antworten[f.id], 'aria-labelledby': labelId, 'aria-describedby': hilfeId,
      'data-nrn-einstieg-regler': f.id, hidden: f.max > REGLER_AUS_AB,
    });
    const feld = h('input', {
      type: 'text', class: ['k-field', 'nrn-konf__zahl'], id: feldId, inputmode: 'numeric', autocomplete: 'off',
      spellcheck: 'false', 'aria-describedby': `${hilfeId} ${fehlerId}`, 'data-nrn-einstieg': f.id,
      value: L.formatZahl(this.#antworten[f.id]),
    });
    const fehlerText = h('span');
    const fehler = h('p', { id: fehlerId, class: 'k-error', hidden: true, 'data-nrn-einstieg-fehler': f.id },
      symbol('achtung'), fehlerText);
    const hilfe = h('p', { id: hilfeId, class: 'k-help' },
      this.#t('feld.grenzen', { min: L.formatZahl(f.min), max: L.formatZahl(f.max) }),
      // Punkt als Trenner, sonst liest sich "0 bis 100.000 1 TB entspricht 1.000 GB" als eine Zahl (Sichtpruefung 08.10.2026)
      f.hint ? `. ${f.hint}` : null);
    this.#el.einstieg[f.id] = { feld, regler, fehler, fehlerText, frage: f };
    return h('div', { class: 'nrn-konf__frage' },
      h('label', { for: feldId, id: labelId, class: 'k-label' }, f.label),
      h('div', { class: 'nrn-konf__frage-steuer' },
        regler,
        h('div', { class: 'nrn-konf__frage-zahl' }, feld, h('span', { class: 'nrn-konf__einheit' }, f.einheit))),
      hilfe, fehler);
  }

  #bauePakete() {
    const titelId = this.#id('pakete_titel');
    const titel = h('h2', { id: titelId, class: 'nrn-konf__h2', tabindex: '-1' }, this.#t('pakete.titel'));
    const karten = PAKETE.map((p) => this.#bauePaketkarte(p));
    // Antworten nach der Übernahme geändert: die Karten zeigen neue Preise, die Summe rechnet mit den Mengen des
    // übernommenen Pakets weiter (KO-02); der Hinweis sagt das (Sichtpruefung 12.10.2026, Befund einstieg-6).
    const geaendert = h('p', {
      class: ['k-hint', 'k-hint--info', 'nrn-konf__pakete-hinweis'], role: 'status', 'data-nrn-pakete-geaendert': '',
      hidden: true,
    });
    const bereich = h('section', {
      class: 'nrn-konf__pakete-bereich', 'aria-labelledby': titelId, 'data-nrn-pakete': '', hidden: true,
    }, titel, geaendert, h('div', { class: 'nrn-konf__pakete' }, karten));
    this.#el.paketBereich = bereich;
    this.#el.paketTitel = titel;
    this.#el.paketGeaendert = geaendert;
    return bereich;
  }

  #bauePaketkarte(p) {
    const titelId = this.#id(`paket_${p}`);
    const name = this.#paketName(p);
    const marken = h('div', { class: 'nrn-konf__paket-marken' },
      h('span', { class: ['k-tier', `k-tier--${STUFE[p]}`] },
        stufenSymbol(),
        h('span', { class: 'k-sr-only' }, this.#t('pakete.stufe', { n: STUFE[p] })),
        h('span', { class: 'k-tier__label' }, name)));
    const preis = h('span', { class: ['k-price', 'nrn-konf__paket-betrag'], 'data-nrn-paketpreis': '' });
    const einheit = h('span', { class: 'k-price__unit' }, this.#t('pakete.proMonat'));
    const jeAp = h('p', { class: ['k-help', 'nrn-konf__paket-jeap'], 'data-nrn-paket-jeap': '', 'data-nrn-preis': '' });
    const liste = h('ul', { class: 'nrn-konf__paket-liste' });
    const knopf = h('button', {
      type: 'button', class: ['k-btn', 'k-btn--rot', 'k-btn--block'], 'data-nrn-aktion': 'paket',
      'data-nrn-paket': p, 'aria-describedby': titelId,
    }, this.#t('pakete.uebernehmen'));
    // Markierung der gewählten Karte (KO-09): sichtbar und per aria-describedby am Knopf, ohne aria-pressed.
    const gewaehltId = this.#id(`paket_${p}_gewaehlt`);
    const gewaehlt = h('span', { class: ['k-badge', 'nrn-konf__paket-badge'], id: gewaehltId, hidden: true },
      symbol('haken'), h('span', {}, this.#t('pakete.abzeichen')));
    marken.append(gewaehlt);
    const karte = h('article', {
      class: ['k-card', 'nrn-konf__paket'], 'data-nrn-paketkarte': p, tabindex: '-1', 'aria-labelledby': titelId,
    },
    marken,
    h('h3', { id: titelId, class: 'nrn-konf__h3' }, name),
    h('p', { class: 'nrn-konf__paket-kurz' }, this.#t(`pakete.kurz.${p}`)),
    h('p', { class: 'nrn-konf__paket-preis', 'data-nrn-preis': '' }, preis, einheit),
    jeAp,
    h('p', { class: 'nrn-konf__paket-enthalten' }, this.#t('pakete.enthalten')),
    liste,
    knopf);
    this.#el.pakete[p] = {
      karte, marken, preis, einheit, jeAp, liste, knopf, badge: null, listeSchluessel: '',
      gewaehlt, beschreibung: titelId, beschreibungGewaehlt: `${titelId} ${gewaehltId}`,
    };
    return karte;
  }

  #paketName(p) {
    const n = this.#einst?.paketNamen?.[p];
    return typeof n === 'string' && n.trim() ? n.trim() : PAKET_NAMEN[p];
  }

  /* ----------------------------------------------------------------------------------------------
   * Aufbau: Feintuning
   * ------------------------------------------------------------------------------------------- */

  #baueFeintuning() {
    const titelId = this.#id('feintuning_titel');
    const titel = h('h2', { id: titelId, class: 'nrn-konf__h2', tabindex: '-1' }, this.#t('feintuning.titel'));
    const badge = h('span', { class: ['k-badge', 'nrn-konf__paket-badge'], hidden: true });
    this.#el.feintuningTitel = titel;
    this.#el.paketBadge = badge;
    const module = [...this.#R.produkte.values()].map((p) => this.#baueModul(p));
    return h('section', { class: 'nrn-konf__feintuning', 'aria-labelledby': titelId },
      h('div', { class: 'nrn-konf__feintuning-kopf' }, titel, badge),
      h('p', { class: 'nrn-konf__text' }, this.#t('feintuning.text')),
      h('div', { class: 'nrn-konf__module' }, module));
  }

  #baueModul(produkt) {
    const id = produkt.id;
    const modulId = this.#id(`modul_${produkt.code}`);
    const panelId = `${modulId}_p`;
    const titelId = `${modulId}_t`;
    const aktivId = `${modulId}_a`;
    const oeffnen = h('button', {
      type: 'button', class: ['k-acc__trigger', 'nrn-konf__modul-knopf'], 'aria-expanded': 'false',
      'aria-controls': panelId, 'data-nrn-produkt-oeffnen': id,
    }, h('span', { class: 'nrn-konf__modul-titel' }, produkt.titel), symbol('chevron', 'k-acc__chevron'));
    const aktiv = h('input', { type: 'checkbox', class: 'nrn-konf__check', id: aktivId, 'data-nrn-produkt-aktiv': id });
    const summe = h('span', { class: ['k-acc__meta', 'nrn-konf__modul-summe'], 'data-nrn-produkt-summe': id, 'data-nrn-preis': '' });
    // Nur der Betrag in Mono, "pro Monat" in DM Sans wie in den Zeilen (Sichtpruefung 12.10.2026, Befund feintuning-11)
    const betrag = h('span', { class: 'k-num' });
    this.#fuelleMit(summe, 'produkt.zwischensumme', {}, 'betrag', betrag);
    const bildUrl = typeof this.#einst.bilder?.[id] === 'string' ? this.#einst.bilder[id] : '';
    let bild = null;
    if (bildUrl) {
      bild = h('img', {
        class: 'nrn-konf__modul-bild', src: bildUrl, alt: '', width: 56, height: 56, loading: 'lazy', decoding: 'async',
      });
      bild.addEventListener('error', () => bild.remove(), { once: true });
    }

    const inhalt = [];
    for (const s of this.#R.schalter) if (s.id.startsWith(`${id}.`)) inhalt.push(this.#baueSchalter(s));
    if (id === 'managedBackup') {
      this.#el.vergleich = h('p', {
        class: ['k-hint', 'k-hint--info', 'nrn-konf__vergleich'], 'data-nrn-vergleich': '', 'data-nrn-preis': '', hidden: true,
      });
      inhalt.push(this.#el.vergleich);
    }
    const felder = [...this.#R.felder.values()].filter((f) => f.product === id).map((f) => this.#baueFeld(f));
    inhalt.push(h('div', { class: 'nrn-konf__felder' }, felder));

    const panel = h('div', {
      class: ['k-acc__panel', 'nrn-konf__modul-panel'], id: panelId, role: 'region', 'aria-labelledby': titelId, hidden: true,
    }, inhalt);
    const modul = h('article', { class: ['k-card', 'nrn-konf__modul'], id: modulId, 'data-nrn-produkt': id },
      h('div', { class: 'nrn-konf__modul-kopf' },
        bild,
        h('h3', { class: 'nrn-konf__modul-h3', id: titelId }, oeffnen),
        h('div', { class: 'nrn-konf__modul-steuer' },
          h('label', { class: 'nrn-konf__einbeziehen', for: aktivId },
            aktiv,
            h('span', {}, this.#t('produkt.einbeziehen')),
            h('span', { class: 'k-sr-only' }, ` ${produkt.titel}`)),
          summe)),
      produkt.kurz ? h('p', { class: 'nrn-konf__modul-kurz' }, produkt.kurz) : null,
      panel);
    this.#el.produkte[id] = { modul, oeffnen, aktiv, summe, betrag, panel, titel: produkt.titel };
    return modul;
  }

  #baueSchalter(s) {
    const name = this.#id(`s_${s.id}`);
    const radios = new Map();
    const karten = s.werte.map((w) => {
      const titelId = this.#id(`s_${s.id}_${w.id}_t`);
      const hintId = this.#id(`s_${s.id}_${w.id}_h`);
      const input = h('input', {
        type: 'radio', name, value: w.id, 'data-nrn-schalter': s.id, 'aria-labelledby': titelId,
        'aria-describedby': w.hint ? hintId : null,
      });
      radios.set(w.id, input);
      return h('label', { class: ['k-choice', 'nrn-konf__wahlkarte'] },
        input,
        h('span', { class: 'k-choice__check', 'aria-hidden': 'true' }, symbol('haken')),
        h('span', { class: 'nrn-konf__wahl-titel', id: titelId }, w.label),
        w.hint ? h('span', { class: ['k-help', 'nrn-konf__wahl-hint'], id: hintId }, w.hint) : null);
    });
    const hinweis = h('p', { class: ['k-help', 'nrn-konf__schalter-hinweis'], hidden: true });
    this.#el.schalter[s.id] = { radios, hinweis };
    return h('fieldset', { class: 'nrn-konf__schalter', 'data-nrn-schaltergruppe': s.id },
      h('legend', { class: 'k-label' }, s.label),
      s.hint ? h('p', { class: 'k-help' }, s.hint) : null,
      h('div', { class: 'nrn-konf__wahl' }, karten),
      hinweis);
  }

  #baueFeld(f) {
    const feldId = this.#id(`f_${f.code}`);
    const labelId = `${feldId}_l`;
    const hilfeId = `${feldId}_h`;
    const zusatzId = `${feldId}_z`;
    const fehlerId = `${feldId}_f`;
    const keys = this.#R.positionenNachFeld.get(f.id) ?? [];
    const mehrere = keys.length > 1;
    const bloecke = keys.map((k) => this.#bauePosition(this.#R.positionen.get(k), mehrere));
    const hint = h('span', { class: 'nrn-konf__hint' });
    const grenzen = h('span', { class: 'nrn-konf__grenzen' });
    // Grenzen samt Punkt als eigener Satzteil: ohne Basis entfällt er, statt "0 bis 0." stehen zu lassen; die
    // Zusatzzeile erklärt dann die Sperre (Kandidat 22).
    const grenzenSatz = h('span', { class: 'nrn-konf__grenzen-satz' }, ' ', grenzen, '.');
    const zusatz = h('div', { id: zusatzId, class: 'nrn-konf__zusatz', 'aria-live': f.maxOf ? 'polite' : null });
    const preise = h('div', { class: 'nrn-konf__feld-preise' }, bloecke);
    const ref = {
      f, hint, grenzen, grenzenSatz, zusatz, zeile: null, input: null, regler: null, minus: null, plus: null, fehler: null,
      fehlerText: null, beschreibung: '',
    };

    if (f.typ === 'checkbox') {
      ref.beschreibung = `${hilfeId} ${zusatzId}`;
      const input = h('input', {
        type: 'checkbox', class: 'nrn-konf__check', id: feldId, 'aria-describedby': ref.beschreibung,
        'data-nrn-feld': f.id,
      });
      ref.input = input;
      ref.zeile = h('div', { class: ['nrn-konf__feld', 'nrn-konf__feld--check'], 'data-nrn-feldzeile': f.id },
        h('div', { class: 'nrn-konf__feld-text' },
          h('label', { class: 'nrn-konf__checklabel', for: feldId, id: labelId },
            input, h('span', { class: 'nrn-konf__checktext' }, f.label)),
          h('p', { id: hilfeId, class: 'k-help' }, hint),
          zusatz),
        preise);
    } else {
      const regler = h('input', {
        type: 'range', class: ['k-slider', 'nrn-konf__regler'], min: 0, max: reglerMax(f.max), step: 1, value: 0,
        'aria-labelledby': labelId, 'aria-describedby': hilfeId, 'data-nrn-regler': f.id, hidden: f.max > REGLER_AUS_AB,
      });
      const minus = h('button', {
        type: 'button', class: 'k-qty__btn', 'aria-label': this.#t('feld.minus', { label: f.label }), 'data-nrn-minus': f.id,
      }, symbol('minus'));
      const plus = h('button', {
        type: 'button', class: 'k-qty__btn', 'aria-label': this.#t('feld.plus', { label: f.label }), 'data-nrn-plus': f.id,
      }, symbol('plus'));
      ref.beschreibung = `${hilfeId} ${zusatzId} ${fehlerId}`;
      const input = h('input', {
        type: 'text', class: 'k-qty__input', id: feldId, inputmode: 'numeric', autocomplete: 'off', spellcheck: 'false',
        'aria-describedby': ref.beschreibung, 'data-nrn-feld': f.id, value: '0',
      });
      const fehlerText = h('span');
      const fehler = h('p', { id: fehlerId, class: 'k-error', hidden: true, 'data-nrn-fehler': f.id },
        symbol('achtung'), fehlerText);
      Object.assign(ref, { input, regler, minus, plus, fehler, fehlerText });
      ref.zeile = h('div', { class: ['nrn-konf__feld', 'nrn-konf__feld--zahl'], 'data-nrn-feldzeile': f.id },
        h('div', { class: 'nrn-konf__feld-text' },
          h('label', { class: 'k-label', for: feldId, id: labelId }, f.label),
          h('p', { id: hilfeId, class: 'k-help' }, hint, grenzenSatz),
          zusatz),
        h('div', { class: 'nrn-konf__feld-steuer' },
          regler,
          h('div', { class: ['k-qty', 'nrn-konf__qty'] }, minus, input, plus),
          fehler),
        preise);
    }
    this.#el.felder[f.id] = ref;
    return ref.zeile;
  }

  #bauePosition(pos, mitLabel) {
    const preis = h('span', { class: 'nrn-konf__einzelpreis', 'data-nrn-preis': '' });
    const summe = h('span', { class: ['k-num', 'nrn-konf__zeilensumme'], 'data-nrn-zeilensumme': pos.key, 'data-nrn-preis': '' });
    const abrechnung = h('span', { class: 'nrn-konf__abrechnung', 'data-nrn-abrechnung': pos.key, 'data-nrn-preis': '', hidden: true });
    const info = h('span', { class: 'nrn-konf__info', hidden: true });
    const gesperrtId = this.#id(`g_${pos.key}`);
    const gesperrt = h('span', {
      class: ['k-error', 'nrn-konf__gesperrt'], id: gesperrtId, 'data-nrn-gesperrt': pos.key, hidden: true,
    }, symbol('schloss'), h('span', {}, this.#t('feld.gesperrt')));
    const block = h('div', { class: 'nrn-konf__position', 'data-nrn-position': pos.key },
      mitLabel ? h('span', { class: 'nrn-konf__position-label' }, pos.label) : null,
      h('span', { class: 'nrn-konf__position-zahlen' }, preis, summe),
      abrechnung, info, gesperrt);
    this.#el.positionen[pos.key] = { pos, block, preis, summe, abrechnung, info, gesperrt, gesperrtId };
    return block;
  }

  /* ----------------------------------------------------------------------------------------------
   * Aufbau: Summe, Abschluss, Dialoge
   * ------------------------------------------------------------------------------------------- */

  #baueSumme() {
    const s = this.#el.summe;
    const detailsId = this.#id('summe_details');
    const sperrId = this.#id('sperrgrund');
    s.netto = h('span', { class: ['k-sum__total', 'nrn-konf__netto'], 'data-nrn-summe': 'netto' });
    s.ohneGesperrte = h('span', { class: 'nrn-konf__ohne-gesperrte', hidden: true }, ` ${this.#t('summe.ohneGesperrte')}`);
    s.pruefen = h('span', { class: ['k-badge', 'k-badge--alert', 'nrn-konf__pruefen'], hidden: true },
      symbol('achtung'), this.#t('summe.eingabePruefen'));
    s.detailsText = h('span', { class: 'k-sr-only' }, this.#t('summe.details'));
    s.detailsKnopf = h('button', {
      type: 'button', class: 'nrn-konf__details-knopf', 'aria-expanded': 'false', 'aria-controls': detailsId,
      'data-nrn-aktion': 'details',
    }, s.detailsText, symbol('chevron', 'nrn-konf__details-chevron'));
    s.kopf = h('div', { class: 'nrn-konf__summe-kopf' },
      h('p', { class: ['k-sum__eyebrow', 'nrn-konf__summe-eyebrow'] }, this.#t('summe.titel')),
      h('p', { class: 'nrn-konf__summe-zahl', 'data-nrn-preis': '' }, s.netto),
      h('p', { class: ['k-sum__unit', 'nrn-konf__summe-einheit'], 'data-nrn-preis': '' }, this.#t('summe.netto'), s.ohneGesperrte),
      s.pruefen,
      s.detailsKnopf);

    const zeile = (schluessel, label) => {
      const lab = h('span', {}, label);
      const wert = h('span', { class: 'k-num', 'data-nrn-summe': schluessel });
      return { row: h('div', { class: ['k-sum__row', 'nrn-konf__summe-zeile'], 'data-nrn-preis': '' }, lab, wert), lab, wert };
    };
    const textZeile = (label) => {
      const wert = h('span', { class: 'nrn-konf__summe-text' });
      return { row: h('div', { class: ['k-sum__row', 'nrn-konf__summe-zeile'], hidden: true }, h('span', {}, label), wert), wert };
    };
    s.ust = zeile('ust', this.#t('summe.ust', { satz: L.formatZahl(this.#R.ustSatz) }));
    s.brutto = zeile('brutto', this.#t('summe.brutto'));
    s.jeAp = zeile('jeAp', this.#t('summe.jeAp'));
    s.jahr = zeile('jahr', this.#t('summe.jahr'));
    s.einrichtung = textZeile(this.#t('summe.einrichtung'));
    s.laufzeit = textZeile(this.#t('summe.laufzeit'));
    s.produkte = h('ul', { class: 'nrn-konf__summe-produkte', 'data-nrn-preis': '' });
    s.abrechnung = h('p', { class: 'nrn-konf__summe-abrechnung', 'data-nrn-preis': '', hidden: true });
    s.nummer = h('span', { class: 'nrn-konf__nummer', 'data-nrn-nummer': '' });
    s.nummerZeile = h('p', { class: 'nrn-konf__summe-nummer' });
    s.sperrgrund = h('p', { class: 'nrn-konf__sperrgrund', id: sperrId, 'data-nrn-sperrgrund': '', tabindex: '-1', hidden: true });
    // Die Liste scrollt ab 990 px in sich (max-height in nrn-konfigurator.css). tabindex 0 macht sie per Tastatur
    // scrollbar, auch wenn sie keine Bedienelemente enthaelt (axe scrollable-region-focusable, Fehlerbehebung 15.10.2026)
    s.details = h('div', {
      class: ['k-sum__details', 'nrn-konf__summe-details'], id: detailsId, tabindex: '0', role: 'group',
      'aria-label': this.#t('summe.details'),
    },
      s.ust.row, s.brutto.row, s.jeAp.row, s.jahr.row, s.einrichtung.row, s.laufzeit.row,
      s.produkte, s.abrechnung, s.nummerZeile, s.sperrgrund);
    s.warenkorb = h('button', {
      type: 'button', class: ['k-btn', 'k-btn--rot', 'nrn-konf__warenkorb'], 'data-nrn-aktion': 'warenkorb',
      'aria-describedby': sperrId,
    }, this.#t('aktion.warenkorb'));
    // Ersatz für den Warenkorbknopf, wenn nur ein Angebot möglich ist (Vertrag P6: ab 990 px per CSS ausgeblendet).
    s.angebot = h('button', {
      type: 'button', class: ['k-btn', 'k-btn--rot', 'nrn-konf__leiste-angebot'], 'data-nrn-aktion': 'angebot',
      'aria-describedby': sperrId, hidden: true,
    }, this.#t('aktion.angebot'));
    s.fehler = h('div', { class: 'nrn-konf__fehlerbereich', 'data-nrn-fehlerbereich': '', tabindex: '-1', hidden: true });
    s.status = h('div', { class: 'nrn-konf__status', role: 'status', 'aria-live': 'polite', 'data-nrn-status': '' });
    // Weissglas mit Rot als Akzent (Kerem 15.10.2026): ohne k-dark, Schrift und Fokus wie auf hellem Grund.
    s.bereich = h('div', { class: ['k-sum', 'nrn-konf__summe'] },
      s.kopf, s.details, s.warenkorb, s.angebot, h('div', { class: 'nrn-konf__meldungen' }, s.fehler, s.status));
    return s.bereich;
  }

  #baueAbschluss() {
    const a = {};
    const knopf = (aktion, key) => h('button', {
      type: 'button', class: ['k-btn', 'k-btn--outline', 'k-btn--block'], 'data-nrn-aktion': aktion,
    }, this.#t(key));
    a.angebot = knopf('angebot', 'aktion.angebot');
    a.pdf = knopf('pdf', 'aktion.pdf');
    a.teilen = knopf('teilen', 'aktion.teilen');
    a.termin = h('a', {
      class: ['k-btn', 'k-btn--outline', 'k-btn--block'], 'data-nrn-aktion': 'termin', href: this.#einst.terminUrl || '#',
      target: '_blank', rel: 'noopener', hidden: !this.#einst.terminUrl,
    }, this.#t('aktion.termin'));
    a.zuruecksetzen = h('button', {
      type: 'button', class: ['k-link', 'nrn-konf__textknopf'], 'data-nrn-aktion': 'zuruecksetzen',
    }, this.#t('aktion.zuruecksetzen'));
    a.linkfeld = h('input', {
      type: 'text', class: ['k-field', 'nrn-konf__linkfeld'], readonly: true, hidden: true,
      'aria-label': this.#t('aktion.teilen'), 'data-nrn-linkfeld': '',
    });
    a.fusszeile = h('p', { class: ['k-help', 'nrn-konf__fusszeile'], hidden: !this.#einst.fusszeile }, this.#einst.fusszeile);
    a.bereich = h('div', { class: 'nrn-konf__abschluss' },
      h('div', { class: 'nrn-konf__abschluss-aktionen' }, a.angebot, a.pdf, a.teilen, a.termin),
      a.linkfeld,
      h('p', { class: 'nrn-konf__abschluss-zuruecksetzen' }, a.zuruecksetzen),
      a.fusszeile);
    this.#el.abschluss = a;
    return a.bereich;
  }

  #baueDialog(name) {
    const titelId = this.#id(`dialog_${name}_t`);
    const textId = this.#id(`dialog_${name}_x`);
    const titel = h('h2', { id: titelId, class: 'nrn-konf__dialog-titel' }, this.#t(`dialog.${name}.titel`));
    const text = h('p', { id: textId, class: 'nrn-konf__dialog-text' });
    const knopf = (aktion, key, klassen) => h('button', {
      type: 'button', class: ['k-btn'].concat(klassen), 'data-nrn-dialog-aktion': aktion,
    }, this.#t(key));
    let knoepfe;
    let start;
    if (name === 'gleich') {
      setzeText(text, this.#t('dialog.gleich.text'));
      const zumWarenkorb = h('a', {
        class: ['k-btn', 'k-btn--silber'], href: this.#warenkorbUrl(), 'data-nrn-dialog-aktion': 'zum-warenkorb',
      }, this.#t('aktion.zumWarenkorb'));
      knoepfe = [zumWarenkorb, knopf('abbrechen', 'dialog.abbrechen', ['k-btn--outline'])];
      start = zumWarenkorb;
    } else if (name === 'ersetzen') {
      const ersetzen = knopf('ersetzen', 'dialog.ersetzen.ersetzen', ['k-btn--silber']);
      knoepfe = [ersetzen, knopf('zusaetzlich', 'dialog.ersetzen.zusaetzlich', ['k-btn--outline']),
        knopf('abbrechen', 'dialog.abbrechen', ['k-btn--outline'])];
      start = ersetzen;
    } else {
      setzeText(text, this.#t('dialog.zuruecksetzen.text'));
      const abbrechen = knopf('abbrechen', 'dialog.abbrechen', ['k-btn--outline']);
      knoepfe = [knopf('bestaetigen', 'dialog.zuruecksetzen.bestaetigen', ['k-btn--rot']), abbrechen];
      start = abbrechen;
    }
    const dialog = h('dialog', {
      class: 'nrn-konf__dialog', 'data-nrn-dialog': name, 'aria-labelledby': titelId, 'aria-describedby': textId,
    }, titel, text, h('div', { class: 'nrn-konf__dialog-aktionen' }, knoepfe));
    this.#el.dialoge[name] = { dialog, text, start };
    return dialog;
  }

  #warenkorbUrl() {
    try {
      return routen().seite || '/cart';
    } catch {
      return '/cart';
    }
  }

  /* ----------------------------------------------------------------------------------------------
   * Aktualisieren
   * ------------------------------------------------------------------------------------------- */

  #hatFeldFehler() {
    return this.#fehler.size > 0;
  }

  /** Rechnet neu und bringt die ganze Oberfläche auf den Stand des Zustands. */
  #aktualisiere() {
    const R = this.#R;
    const b = L.berechne(this.#z, R, this.#ks, this.#einst, this.#texte, { eingabeFehler: this.#hatFeldFehler() });
    this.#b = b;
    const vorher = this.#nummer;
    this.#nummer = L.konfigNummer(this.#z, R);
    this.#raeumeMeldungenAuf(vorher);
    const ohnePreise = this.#ks.modus === 'nurAngebot';
    this.classList.toggle('nrn-konf--ohne-preise', ohnePreise);

    this.#aktualisiereHinweise();
    this.#aktualisiereEinstieg();
    this.#aktualisierePakete();
    this.#aktualisiereFeintuning(b);
    this.#aktualisiereSumme(b);
    this.#aktualisiereDruck(b);
    this.#planeAnsage(b);
  }

  #nummer = '';
  /** Nummer, die die sichtbare Erfolgsmeldung nennt (null: keine Erfolgsmeldung sichtbar). */
  #erfolgNummer = null;
  /** Der Fehlerbereich zeigt "alte Konfiguration nicht entfernt" (bleibt, solange #offeneAlte Einträge hat). */
  #fehlerAlteOffen = false;

  /**
   * Meldungen, die zu einer anderen Konfiguration gehören, verschwinden (KO-05): die Erfolgsmeldung,
   * sobald die Nummer von der gemeldeten abweicht; Warenkorbfehler bei jeder Änderung der Nummer, außer
   * dem Hinweis auf nicht entfernte alte Konfigurationen, solange diese noch offen sind.
   */
  #raeumeMeldungenAuf(vorher) {
    if (this.#erfolgNummer !== null && this.#erfolgNummer !== this.#nummer) this.#zeigeStatus('');
    if (!vorher || vorher === this.#nummer || this.#el.summe.fehler.hidden) return;
    if (this.#fehlerAlteOffen && this.#offeneAlte.length) return;
    this.#zeigeFehler('');
  }

  #aktualisiereHinweise() {
    const e = this.#el.hinweise;
    const eintraege = this.#hinweise.map((x) => h('li', {}, this.#t(`hinweise.${x.code}`, x.params)));
    const schluessel = eintraege.map((x) => x.textContent).join('\n');
    if (e.liste.dataset.nrnSchluessel !== schluessel) {
      e.liste.replaceChildren(...eintraege);
      e.liste.dataset.nrnSchluessel = schluessel;
    }
    setzeVerborgen(e.verwerfen, !this.#hinweisVerwerfen);
    setzeVerborgen(e.bereich, eintraege.length === 0);
  }

  #meldung(r) {
    const s = this.#t(`meldung.${r.code}`, r.params);
    if (!s.startsWith('⟦')) return s;
    // Rückfall nur für unbekannte Codes; "min" hat seit TX-06 einen eigenen Text.
    return this.#t('sperre.eingabe');
  }

  #aktualisiereEinstieg() {
    let amLimit = false;
    const aktiv = document.activeElement;
    for (const f of this.#R.einstieg) {
      const el = this.#el.einstieg[f.id];
      const v = this.#antworten[f.id];
      const fe = this.#einstiegFehler.get(f.id);
      if (!fe && aktiv !== el.feld) {
        const anzeige = L.formatZahl(v);
        if (el.feld.value !== anzeige) el.feld.value = anzeige;
      }
      this.#setzeRegler(el.regler, v, f.min, f.max, f.einheit);
      setzeAttr(el.feld, 'aria-invalid', fe ? 'true' : null);
      // Über der Grenze nennt das Feld nur den Bereich; den Angebotssatz sagt der Grenzhinweis darunter einmal für alle
      // Fragen (vorher dreimal derselbe Satz; Sichtpruefung 12.10.2026, Befund einstieg-8). Feintuning behält meldung.max.
      const meldung = fe?.code === 'max'
        ? this.#t('meldung.min', { min: L.formatZahl(f.min), max: fe.params.max })
        : (fe ? this.#meldung(fe) : '');
      setzeText(el.fehlerText, meldung);
      setzeVerborgen(el.fehler, !fe);
      if (v >= f.max || fe?.code === 'max') amLimit = true;
    }
    setzeVerborgen(this.#el.grenze, !amLimit);
  }

  #setzeRegler(regler, wert, min, max, einheit) {
    const mx = Math.max(min, max);
    const v = Math.min(Math.max(wert, min), mx);
    setzeAttr(regler, 'min', String(min));
    setzeAttr(regler, 'max', String(reglerMax(mx)));
    if (regler.value !== String(v)) regler.value = String(v);
    regler.style.setProperty('--slider-progress', `${reglerFortschritt(v, min, mx).toFixed(2)}%`);
    setzeAttr(regler, 'aria-valuetext', einheit ? `${L.formatZahl(v)} ${einheit}` : L.formatZahl(v));
  }

  #aktualisierePakete() {
    setzeVerborgen(this.#el.paketBereich, !this.#paketeSichtbar);
    if (!this.#paketeSichtbar) return;
    const schluessel = JSON.stringify(this.#antworten);
    const c = this.#paketCache;
    if (c.schluessel !== schluessel || !c.preise) {
      c.preise = L.paketPreise(this.#antworten, this.#R, this.#ks, this.#einst, this.#texte);
      c.empfohlen = L.empfohlenesPaket(this.#antworten);
      c.schluessel = schluessel;
    }
    for (const p of PAKETE) {
      const el = this.#el.pakete[p];
      const preis = c.preise[p];
      // Gesperrte Position im Paket: Preis unbekannt, wie an der Position "Preis auf Anfrage" statt einer zu kleinen
      // Summe und ohne Betrag je Arbeitsplatz (Befund 6).
      const aufAnfrage = preis.nettoCent === null;
      setzeText(el.preis, aufAnfrage ? this.#t('feld.preisAufAnfrage') : L.formatCent(preis.nettoCent));
      setzeAttr(el.preis, 'data-nrn-cent', aufAnfrage ? null : String(preis.nettoCent));
      setzeAttr(el.preis, 'data-nrn-auf-anfrage', aufAnfrage);
      setzeVerborgen(el.einheit, aufAnfrage);
      setzeText(el.jeAp, preis.jeApCent === null ? '' : this.#t('pakete.jeAp', { betrag: L.formatCent(preis.jeApCent) }));
      setzeVerborgen(el.jeAp, preis.jeApCent === null);
      const listeSchluessel = preis.zeilen.map((z) => `${z.key}:${z.quantity}:${z.grundlage ?? ''}`)
        .concat((preis.hinweise ?? []).map((x) => `!${x.code}:${JSON.stringify(x.params ?? {})}`)).join('|');
      if (el.listeSchluessel !== listeSchluessel) {
        el.liste.replaceChildren(...this.#paketListe(preis.zeilen, preis.hinweise));
        el.listeSchluessel = listeSchluessel;
      }
      const empfohlen = c.empfohlen === p;
      el.karte.classList.toggle('k-card--featured', empfohlen);
      if (empfohlen && !el.badge) {
        el.badge = h('span', { class: ['k-badge', 'k-badge--rot'], 'data-nrn-empfohlen': '' }, this.#t('pakete.passend'));
        el.marken.append(el.badge);
      } else if (!empfohlen && el.badge) {
        el.badge.remove();
        el.badge = null;
      }
      const gewaehlt = this.#z.paket === p;
      el.karte.classList.toggle('nrn-konf__paket--gewaehlt', gewaehlt);
      setzeVerborgen(el.gewaehlt, !gewaehlt);
      // "Passend fuer Sie" tritt hinter "Uebernommen" zurueck: zwei Chips passen in eine Zeile, drei nicht (Befund 4)
      if (el.badge) setzeVerborgen(el.badge, gewaehlt);
      setzeAttr(el.knopf, 'aria-describedby', gewaehlt ? el.beschreibungGewaehlt : el.beschreibung);
    }
    const geaendert = Boolean(this.#uebernommen) && this.#z.paket === 'individuell' && Boolean(this.#z.einstieg)
      && this.#antwortSchluessel(this.#z.einstieg) !== this.#uebernommenAntworten;
    setzeText(this.#el.paketGeaendert,
      geaendert ? this.#t('pakete.geaendert', { paket: this.#paketName(this.#uebernommen) }) : '');
    setzeVerborgen(this.#el.paketGeaendert, !geaendert);
  }

  /** Vergleichbarer Schlüssel der Einstiegsantworten in fester Fragenreihenfolge. */
  #antwortSchluessel(antworten) {
    return this.#R.einstieg.map((f) => antworten?.[f.id] ?? '').join('|');
  }

  #paketListe(zeilen, hinweise) {
    const gruppen = new Map();
    for (const z of zeilen) {
      if (!gruppen.has(z.produkt)) gruppen.set(z.produkt, []);
      gruppen.get(z.produkt).push(z);
    }
    // Pauschale Position (Regel flag) mit ihrer Einheit statt "Kundenzugang: 1", wie Druck, Hinweis und Variantentitel
    // (Kandidaten 63 und 81).
    const menge = (z) => z.grundlage
      ?? (z.regel === 'flag' ? (this.#R.positionen.get(z.key)?.unit ?? L.formatZahl(z.quantity)) : L.formatZahl(z.quantity));
    const eintraege = [];
    for (const id of this.#R.produktReihenfolge) {
      const liste = gruppen.get(id);
      if (!liste) continue;
      eintraege.push(h('li', { class: 'nrn-konf__paket-gruppe' },
        h('span', { class: 'nrn-konf__paket-produkt' }, this.#R.produkte.get(id).titel),
        h('ul', {}, liste.map((z) => h('li', {}, this.#t('pakete.zeile', { label: z.label, menge: menge(z) }))))));
    }
    // Hinweise der Empfehlung (etwa ab 76 Arbeitsplätzen je Standort) schon beim Vergleich, nicht erst nach dem
    // Übernehmen; als Eintrag der Liste, damit das Raster der Karte (span 8) bleibt (Kandidat 79).
    for (const x of hinweise ?? []) {
      eintraege.push(h('li', { class: ['k-help', 'nrn-konf__paket-hinweis'] }, this.#t(`hinweise.${x.code}`, x.params)));
    }
    return eintraege;
  }

  /** Positionen eines Felds, die unter den aktuellen Schaltern wirken würden (Produkt einbezogen gedacht). */
  #relevantePositionen(feldId) {
    const z = this.#z;
    const gedacht = { ...z, produkte: Object.fromEntries(Object.keys(z.produkte).map((k) => [k, true])) };
    return (this.#R.positionenNachFeld.get(feldId) ?? []).filter((k) => L.positionAktiv(k, gedacht, this.#R));
  }

  #status(key) {
    return this.#ks.positionen[key] ?? { status: 'gesperrt', grund: 'fehlt', preisCent: null };
  }

  #aktualisiereFeintuning(b) {
    const R = this.#R;
    const z = this.#z;
    const aktiv = document.activeElement;
    const zeilenNachKey = new Map(b.zeilen.map((x) => [x.key, x]));
    const infoNachKey = new Map(b.infoZeilen.map((x) => [x.key, x]));

    if (z.paket) {
      setzeText(this.#el.paketBadge, z.paket === 'individuell' ? this.#t('pakete.individuell') : this.#paketName(z.paket));
    }
    setzeVerborgen(this.#el.paketBadge, !z.paket);

    // Produkte
    for (const [id, el] of Object.entries(this.#el.produkte)) {
      const offen = this.#offen.has(id);
      el.aktiv.checked = Boolean(z.produkte[id]);
      el.modul.classList.toggle('nrn-konf__modul--inaktiv', !z.produkte[id]);
      setzeText(el.betrag, L.formatCent(b.proProdukt[id] ?? 0));
      setzeAttr(el.oeffnen, 'aria-expanded', offen ? 'true' : 'false');
      setzeAttr(el.oeffnen, 'aria-label', this.#t(offen ? 'produkt.schliessen' : 'produkt.oeffnen', { produkt: el.titel }));
      setzeVerborgen(el.panel, !offen);
    }

    // Schalter
    for (const [id, el] of Object.entries(this.#el.schalter)) {
      for (const [wert, input] of el.radios) input.checked = z.schalter[id] === wert;
      if (id === 'managedBackup.storage') {
        const gleich = z.schalter['managedBackup.billingModel'] === 'gigabyte';
        setzeText(el.hinweis, gleich ? this.#t('schalter.gigabyteGleich') : '');
        setzeVerborgen(el.hinweis, !gleich);
      }
    }

    // Vergleich Backup Modell
    if (this.#el.vergleich) {
      const v = L.backupVergleich(z, R, this.#ks, this.#einst, this.#texte);
      setzeText(this.#el.vergleich, v ? this.#t(`schalter.vergleich.${v.modell}`, { betrag: L.formatCent(v.nettoCent) }) : '');
      setzeVerborgen(this.#el.vergleich, !v);
    }

    // Felder und Positionen
    for (const [feldId, el] of Object.entries(this.#el.felder)) {
      const f = el.f;
      const sichtbar = L.feldSichtbar(feldId, z, R);
      setzeVerborgen(el.zeile, !sichtbar);
      const alle = R.positionenNachFeld.get(feldId) ?? [];
      const relevant = this.#relevantePositionen(feldId);
      const gesperrt = relevant.length > 0 && relevant.every((k) => this.#status(k).status === 'gesperrt');
      const wert = z.felder[feldId] ?? 0;
      let max = f.max;
      let basisFehlt = false;
      if (f.maxOf) {
        const basis = z.felder[f.maxOf] ?? 0;
        max = Math.min(f.max, basis);
        basisFehlt = basis === 0;
      }

      // Hinweistext der wirksamen Position, sonst der ersten
      const hintPos = R.positionen.get(relevant[0] ?? alle[0]);
      setzeText(el.hint, hintPos?.hint ?? '');

      // Zusatzzeilen
      const zusatz = [];
      if (basisFehlt) {
        // Mitten im Satz der Name aus bezugImSatz ("virtuelle Maschinen"), sonst das Label (Befund 21)
        const basisFeld = R.felder.get(f.maxOf);
        zusatz.push(this.#t('feld.basisFehlt', { bezug: basisFeld.bezugImSatz ?? basisFeld.label }));
      }
      // Nur, wenn der Hilfetext den Satz nicht schon enthält, sonst steht er zweimal untereinander (Befund feintuning-8).
      // Eigener Text je Feld, falls die Textdatei ihn kennt (Microsoft 365 ist kein Gerät, Befund 50).
      if (relevant.length === 0 && f.product === 'managedBackup' && z.schalter['managedBackup.billingModel'] === 'gigabyte') {
        const eigen = this.#t(`feld.ohneGeraetepreis.${feldId}`);
        const ohnePreis = eigen.startsWith('⟦') ? this.#t('feld.ohneGeraetepreis') : eigen;
        if (!(hintPos?.hint ?? '').includes(ohnePreis)) zusatz.push(ohnePreis);
      }
      // Ist die Bezugsposition gesperrt, rechnet die Folgeposition mit 0; "= 25 Postfächer" neben 0,00 € widerspräche
      // dem, den Grund nennt der Sperrtext an der Bezugsposition (Kandidat 42). Sonst bleibt der Rohwert als Vorschau.
      const bezug = f.follows ? this.#relevantePositionen(f.follows) : [];
      const bezugGesperrt = bezug.length > 0 && bezug.every((k) => this.#status(k).status === 'gesperrt');
      if (f.follows && !bezugGesperrt) {
        const q = z.felder[f.follows] ?? 0;
        // Einzahl je Feld, falls die Textdatei sie kennt (X-02), sonst die allgemeine Folgezeile.
        const eins = q === 1 ? this.#t(`feld.folgtMenge.eins.${feldId}`) : null;
        if (q <= 0) zusatz.push(this.#t(`feld.folgtLeer.${feldId}`));
        else if (eins && !eins.startsWith('⟦')) zusatz.push(eins);
        else zusatz.push(this.#t('feld.folgtMenge', { q: L.formatZahl(q), einheit: f.einheit }));
      }
      if (this.#feldHinweise.has(feldId)) zusatz.push(this.#feldHinweise.get(feldId));
      const zusatzSchluessel = zusatz.join('\n');
      if (el.zusatz.dataset.nrnSchluessel !== zusatzSchluessel) {
        el.zusatz.replaceChildren(...zusatz.map((t) => h('p', { class: 'nrn-konf__zusatzzeile' }, t)));
        el.zusatz.dataset.nrnSchluessel = zusatzSchluessel;
      }

      // Sperrtexte der wirksamen gesperrten Positionen beschreiben das Eingabefeld mit (X-01).
      const sperrIds = relevant.filter((k) => this.#status(k).status === 'gesperrt')
        .map((k) => this.#el.positionen[k].gesperrtId);
      setzeAttr(el.input, 'aria-describedby', [el.beschreibung, ...sperrIds].join(' '));

      // Sperren (KO-03): aria-disabled hält ein fokussiertes Element erreichbar; disabled nur, wenn alles
      // gesperrt ist und das Element gerade nicht den Fokus hat. Abgefangen wird in #beiKlick,
      // #reglerEingabe und #beiAenderung.
      if (f.typ === 'checkbox') {
        el.input.checked = wert === 1;
        const aus = gesperrt && wert === 0;
        setzeAttr(el.input, 'aria-disabled', aus ? 'true' : null);
        el.input.disabled = aus && aktiv !== el.input;
      } else {
        const fe = this.#fehler.get(feldId);
        if (!fe && aktiv !== el.input) {
          const anzeige = L.formatZahl(wert);
          if (el.input.value !== anzeige) el.input.value = anzeige;
        }
        this.#setzeRegler(el.regler, wert, 0, max, f.einheit);
        setzeText(el.grenzen, this.#t('feld.grenzen', { min: '0', max: L.formatZahl(max) }));
        setzeVerborgen(el.grenzenSatz, basisFehlt);
        const nurSenken = gesperrt && wert > 0;
        const alles = basisFehlt || (gesperrt && wert === 0);
        const eingabeAus = alles && !fe;
        setzeAttr(el.input, 'aria-disabled', eingabeAus ? 'true' : null);
        el.input.readOnly = eingabeAus && aktiv === el.input;
        el.input.disabled = eingabeAus && aktiv !== el.input;
        setzeSperre(el.regler, alles || nurSenken, alles, aktiv);
        // Über der Grenze: Minus frei (führt auf die Höchstmenge), Plus gesperrt (Befund feintuning-6)
        const ueber = fe?.code === 'max' || fe?.code === 'maxOf';
        setzeSperre(el.minus, alles || (wert <= 0 && !ueber), alles, aktiv);
        setzeSperre(el.plus, alles || nurSenken || wert >= max || ueber, alles, aktiv);
        setzeAttr(el.input, 'aria-invalid', fe ? 'true' : null);
        setzeText(el.fehlerText, fe ? this.#meldung(fe) : '');
        setzeVerborgen(el.fehler, !fe);
      }

      for (const key of alle) this.#aktualisierePosition(key, relevant.includes(key), wert, zeilenNachKey, infoNachKey);
    }
  }

  #aktualisierePosition(key, relevant, wert, zeilenNachKey, infoNachKey) {
    const el = this.#el.positionen[key];
    const s = this.#status(key);
    setzeVerborgen(el.block, !relevant);
    if (!relevant) return;
    if (s.status === 'info') {
      const info = infoNachKey.get(key);
      setzeText(el.info, info ? info.text : '');
      setzeVerborgen(el.info, !info);
      setzeVerborgen(el.preis, true);
      setzeVerborgen(el.summe, true);
      setzeVerborgen(el.abrechnung, true);
      setzeVerborgen(el.gesperrt, true);
      // Ohne Infozeile bliebe nur das Positionslabel ohne Wert stehen (Befund feintuning-9)
      setzeVerborgen(el.block, !info);
      return;
    }
    setzeVerborgen(el.info, true);
    if (s.status === 'gesperrt') {
      setzeText(el.preis, this.#t('feld.preisAufAnfrage'));
      setzeVerborgen(el.preis, false);
      setzeText(el.summe, '');
      setzeVerborgen(el.summe, true);
      setzeVerborgen(el.abrechnung, true);
      setzeVerborgen(el.gesperrt, false);
      return;
    }
    setzeVerborgen(el.gesperrt, true);
    setzeText(el.preis, this.#t('feld.einzelpreis', { preis: L.formatCent(s.preisCent) }));
    setzeVerborgen(el.preis, false);
    const zeile = zeilenNachKey.get(key);
    const cent = zeile ? zeile.zeileCent : 0;
    setzeText(el.summe, L.formatCent(cent));
    el.summe.dataset.nrnCent = String(cent);
    setzeVerborgen(el.summe, false);
    const abr = L.abrechnungszeile(el.pos, wert, s.preisCent, this.#texte);
    setzeText(el.abrechnung, abr ?? '');
    setzeVerborgen(el.abrechnung, !abr);
  }

  #aktualisiereSumme(b) {
    const s = this.#el.summe;
    const R = this.#R;
    const geaendert = this.#letzteNetto !== null && this.#letzteNetto !== b.nettoCent;
    setzeText(s.netto, L.formatCent(b.nettoCent));
    s.netto.dataset.nrnCent = String(b.nettoCent);
    if (geaendert && !ruhig()) {
      s.kopf.classList.remove('k-sum__flash');
      void s.kopf.offsetWidth;
      s.kopf.classList.add('k-sum__flash');
    }
    this.#letzteNetto = b.nettoCent;
    setzeVerborgen(s.ohneGesperrte, b.blockiert.length === 0);
    setzeVerborgen(s.pruefen, !this.#hatFeldFehler());

    const wert = (zeile, cent) => {
      setzeText(zeile.wert, L.formatCent(cent));
      zeile.wert.dataset.nrnCent = String(cent);
    };
    wert(s.ust, b.ustCent);
    wert(s.brutto, b.bruttoCent);
    wert(s.jahr, b.jahrCent);
    if (b.jeApCent === null) setzeText(s.jeAp.wert, '');
    else wert(s.jeAp, b.jeApCent);
    setzeVerborgen(s.jeAp.row, b.jeApCent === null);
    setzeText(s.einrichtung.wert, this.#einst.einrichtungText);
    setzeVerborgen(s.einrichtung.row, !this.#einst.einrichtungText);
    setzeText(s.laufzeit.wert, this.#einst.laufzeitText);
    setzeVerborgen(s.laufzeit.row, !this.#einst.laufzeitText);

    // je Produkt, Klick springt zum Abschnitt
    const produkte = R.produktReihenfolge.filter((id) => this.#z.produkte[id]);
    const pSchluessel = produkte.map((id) => `${id}:${b.proProdukt[id]}`).join('|');
    if (s.produkte.dataset.nrnSchluessel !== pSchluessel) {
      s.produkte.replaceChildren(...produkte.map((id) => h('li', {},
        h('button', { type: 'button', class: 'nrn-konf__sprung', 'data-nrn-sprung': id },
          h('span', { class: 'nrn-konf__sprung-titel' }, R.produkte.get(id).titel),
          h('span', { class: 'k-num' }, L.formatCent(b.proProdukt[id] ?? 0))))));
      s.produkte.dataset.nrnSchluessel = pSchluessel;
    }
    setzeVerborgen(s.produkte, produkte.length === 0);

    // Abrechnung
    let abrechnung = '';
    if (b.abrechnung.art === 'abo' && b.abrechnung.planNamen.length) {
      abrechnung = this.#t('summe.abrechnungPlan', { plan: b.abrechnung.planNamen.join(', ') });
    } else if (b.abrechnung.art === 'erstmonat' && b.zeilen.length) {
      abrechnung = this.#einst.hinweisAbrechnung;
    }
    setzeText(s.abrechnung, abrechnung);
    setzeVerborgen(s.abrechnung, !abrechnung);

    // Nummer
    const nummer = L.formatNummer(this.#nummer);
    if (s.nummer.textContent !== nummer || !s.nummer.isConnected) {
      setzeText(s.nummer, nummer);
      this.#fuelleMit(s.nummerZeile, 'summe.nummer', {}, 'nummer', s.nummer);
    }

    // Sperrgrund und Knöpfe
    setzeText(s.sperrgrund, b.sperrgrund ? this.#t(`sperre.${b.sperrgrund}`) : '');
    setzeVerborgen(s.sperrgrund, !b.sperrgrund);
    // Grund als Attribut: "leer" (noch nichts gewaehlt) ist ein neutraler Hinweis, kein Fehlerton (Sichtpruefung 08.10.2026)
    setzeAttr(s.sperrgrund, 'data-nrn-grund', b.sperrgrund || null);
    this.#aktualisiereKnopf();
    const angebotPrimaer = ['gesperrt', 'abrechnung', 'nurAngebot', 'invariante'].includes(b.sperrgrund);
    const a = this.#el.abschluss;
    a.angebot.classList.toggle('k-btn--rot', angebotPrimaer);
    a.angebot.classList.toggle('k-btn--outline', !angebotPrimaer);
    // Höchstens eine rote Hauptaktion je Zustand (VD-02, seit 15.10.2026 Rot statt Silber): ist das Angebot
    // primär, tritt der Warenkorb als Nebenaktion in Silber zurück (dann ohnehin gesperrt).
    s.warenkorb.classList.toggle('k-btn--rot', !angebotPrimaer);
    s.warenkorb.classList.toggle('k-btn--silber', angebotPrimaer);
  }

  #aktualisiereKnopf() {
    const s = this.#el.summe;
    const b = this.#b;
    const nurAngebot = b.sperrgrund === 'nurAngebot';
    setzeVerborgen(s.warenkorb, nurAngebot);
    // Vertrag P6: der Angebotsknopf der Leiste ersetzt den verborgenen Warenkorbknopf.
    setzeVerborgen(s.angebot, !(nurAngebot || this.#ks.modus === 'nurAngebot'));
    setzeAttr(s.warenkorb, 'aria-disabled', (!b.warenkorbErlaubt || this.#beschaeftigt) ? 'true' : null);
    setzeAttr(s.warenkorb, 'aria-busy', this.#beschaeftigt ? 'true' : null);
  }

  /** Füllt ziel mit dem Text key, wobei {name} durch das Element ersetzt wird. */
  #fuelleMit(ziel, key, params, name, element) {
    const vorlage = this.#t(key);
    const teile = vorlage.split(`{${name}}`);
    const knoten = [];
    teile.forEach((teil, i) => {
      if (i > 0) knoten.push(element);
      if (teil) knoten.push(L.text({ v: teil }, 'v', params));
    });
    if (teile.length === 1) knoten.push(' ', element);
    ziel.replaceChildren(...knoten);
  }

  #planeAnsage(b) {
    // Nur Angebot: keine Preise, also auch keine Ansage der Summe (KO-01).
    if (this.#ks.modus === 'nurAngebot') return;
    if (this.#angesagt === null) {
      this.#angesagt = b.nettoCent;
      return;
    }
    clearTimeout(this.#timer.ansage);
    this.#timer.ansage = setTimeout(() => {
      const netto = this.#b?.nettoCent;
      if (netto === undefined || netto === this.#angesagt) return;
      this.#angesagt = netto;
      setzeText(this.#el.ansage, this.#t('summe.ansage', { zahl: ZAHL_ZWEI.format(netto / 100) }));
    }, ANSAGE_VERZOEGERUNG);
  }

  /* ----------------------------------------------------------------------------------------------
   * Druckansicht
   * ------------------------------------------------------------------------------------------- */

  #aktualisiereDruck(b) {
    const R = this.#R;
    const datum = DATUM.format(new Date());
    const hash = L.serialisiere(this.#z, R);
    const schluessel = [hash, b.nettoCent, b.ustCent, b.blockiert.length, b.infoZeilen.length, datum].join('|');
    if (schluessel === this.#druckSchluessel) return;
    this.#druckSchluessel = schluessel;

    // Nur Angebot (KO-01): ohne Menge, Preise, Summen und Abrechnungshinweis, dafür der Sperrhinweis.
    const ohnePreise = this.#ks.modus === 'nurAngebot';
    const nachKey = new Map(b.zeilen.map((x) => [x.key, x]));
    const gesperrtNachKey = new Map(b.blockiert.map((x) => [x.key, x]));
    const infoNachKey = new Map(b.infoZeilen.map((x) => [x.key, x]));
    const spalten = ohnePreise ? ['leistung', 'eingabe'] : ['leistung', 'eingabe', 'menge', 'einzelpreis', 'summe'];
    const kopf = h('thead', {}, h('tr', {}, spalten.map((sp, i) => h('th', {
      scope: 'col', class: i >= 2 ? 'nrn-druck__zahl' : null,
    }, this.#t(`druck.spalte.${sp}`)))));

    const tabellen = [];
    for (const id of R.produktReihenfolge) {
      const zeilen = [];
      for (const pos of R.positionen.values()) {
        if (pos.product !== id) continue;
        const feld = R.felder.get(pos.feld);
        const x = nachKey.get(pos.key);
        const g = gesperrtNachKey.get(pos.key);
        const info = infoNachKey.get(pos.key);
        if (x) {
          zeilen.push(h('tr', {},
            h('th', { scope: 'row' }, x.label, x.grundlage ? h('span', { class: 'nrn-druck__grundlage' }, x.grundlage) : null),
            h('td', {}, this.#eingabeText(feld, x.eingabe)),
            ohnePreise ? null : [
              h('td', { class: 'nrn-druck__zahl' }, L.formatZahl(x.quantity)),
              h('td', { class: 'nrn-druck__zahl' }, L.formatCent(x.preisCent)),
              h('td', { class: 'nrn-druck__zahl' }, L.formatCent(x.zeileCent))]));
        } else if (g) {
          zeilen.push(h('tr', {},
            h('th', { scope: 'row' }, pos.label),
            h('td', {}, this.#eingabeText(feld, g.eingabe)),
            // eine Zelle über Menge, Einzelpreis und Summe: "Preis auf Anfrage" lief in der schmalen Spalte
            // Einzelpreis in die Spalte Summe (Sichtpruefung 12.10.2026, abschluss übersehen)
            ohnePreise ? null : h('td', { class: 'nrn-druck__zahl', colspan: '3' }, this.#t('feld.preisAufAnfrage'))));
        } else if (info) {
          zeilen.push(h('tr', {},
            h('th', { scope: 'row' }, pos.label, h('span', { class: 'nrn-druck__grundlage' }, info.text)),
            h('td', {}, this.#eingabeText(feld, this.#z.felder[pos.feld])),
            ohnePreise ? null : [h('td', {}), h('td', {}), h('td', {})]));
        }
      }
      if (!zeilen.length) continue;
      tabellen.push(h('section', { class: 'nrn-druck__produkt' },
        h('h2', {}, R.produkte.get(id).titel),
        h('table', { class: 'nrn-druck__tabelle' },
          kopf.cloneNode(true),
          h('tbody', {}, zeilen),
          // Zwischensumme über Einzelpreis und Summe, sonst schnitt die Seite "149,89 € pro Monat" rechts ab
          // (Sichtpruefung 12.10.2026, Befund abschluss-3)
          ohnePreise ? null : h('tfoot', {}, h('tr', {},
            h('td', { colspan: '3' }),
            h('td', { class: 'nrn-druck__zahl', colspan: '2' }, this.#t('produkt.zwischensumme', { betrag: L.formatCent(b.proProdukt[id] ?? 0) })))))));
    }

    if (ohnePreise) {
      fuelle(this.#el.druck,
        this.#druckKopf(datum),
        tabellen,
        h('p', { class: 'nrn-druck__abrechnung' }, this.#t('sperre.nurAngebot')),
        h('p', { class: 'nrn-druck__link' }, this.#t('druck.link', { url: this.#linkUrl(hash) })),
        this.#einst.fusszeile ? h('p', { class: 'nrn-druck__fuss' }, this.#einst.fusszeile) : null);
      return;
    }

    // Textzeilen (Einrichtung, Laufzeit) ohne Zahlenklasse: sie duerfen umbrechen und stehen in der Fliesstextschrift
    const summe = (label, wert, text = false) => h('tr', {}, h('th', { scope: 'row' }, label),
      h('td', { class: text ? 'nrn-druck__text' : 'nrn-druck__zahl' }, wert));
    const summen = [
      summe(this.#t('summe.netto') + (b.blockiert.length ? ` (${this.#t('summe.ohneGesperrte')})` : ''), L.formatCent(b.nettoCent)),
      summe(this.#t('summe.ust', { satz: L.formatZahl(R.ustSatz) }), L.formatCent(b.ustCent)),
      summe(this.#t('summe.brutto'), L.formatCent(b.bruttoCent)),
      b.jeApCent === null ? null : summe(this.#t('summe.jeAp'), L.formatCent(b.jeApCent)),
      summe(this.#t('summe.jahr'), L.formatCent(b.jahrCent)),
      this.#einst.einrichtungText ? summe(this.#t('summe.einrichtung'), this.#einst.einrichtungText, true) : null,
      this.#einst.laufzeitText ? summe(this.#t('summe.laufzeit'), this.#einst.laufzeitText, true) : null,
    ];
    let abrechnung = '';
    if (b.abrechnung.art === 'abo' && b.abrechnung.planNamen.length) {
      abrechnung = this.#t('summe.abrechnungPlan', { plan: b.abrechnung.planNamen.join(', ') });
    } else if (b.abrechnung.art === 'erstmonat' && b.zeilen.length) {
      // wie in der Summe nur mit mindestens einer Zeile: ohne Position gibt es keinen ersten Monat (Kandidat 104)
      abrechnung = this.#einst.hinweisAbrechnung;
    }

    fuelle(this.#el.druck,
      this.#druckKopf(datum),
      tabellen,
      h('table', { class: ['nrn-druck__tabelle', 'nrn-druck__summen'] }, h('tbody', {}, summen)),
      abrechnung ? h('p', { class: 'nrn-druck__abrechnung' }, abrechnung) : null,
      h('p', { class: 'nrn-druck__link' }, this.#t('druck.link', { url: this.#linkUrl(hash) })),
      this.#einst.fusszeile ? h('p', { class: 'nrn-druck__fuss' }, this.#einst.fusszeile) : null);
  }

  #druckKopf(datum) {
    return h('header', { class: 'nrn-druck__kopf' },
      h('p', { class: 'nrn-druck__marke' }, MARKE),
      h('h1', { class: 'nrn-druck__titel' }, this.#t('druck.titel')),
      h('p', { class: 'nrn-druck__meta' },
        this.#t('druck.datum', { datum }), h('br'),
        this.#t('summe.nummer', { nummer: L.formatNummer(this.#nummer) })));
  }

  #eingabeText(feld, n) {
    if (!feld) return '';
    // Menge 1 in der Einzahl, falls die Textdatei sie kennt ("1 Ziel" statt "1 Ziele", Befund abschluss-8)
    if (feld.typ === 'zahl') return L.eingabeMitEinheit(feld, n, this.#texte);
    return feld.follows ? '' : feld.einheit;
  }

  #linkUrl(hash) {
    let basis = this.#einst.seitenUrl;
    if (!/^https?:\/\//i.test(basis)) {
      try {
        basis = `${window.location.origin}${window.location.pathname}`;
      } catch {
        basis = '';
      }
    }
    return `${basis.replace(/#.*$/, '')}#${hash}`;
  }

  #vorDruck() {
    this.#schreibeHashJetzt();
    if (this.#b) {
      this.#druckSchluessel = '';
      this.#aktualisiereDruck(this.#b);
    }
    if (this.#titelVorDruck === null) {
      this.#titelVorDruck = document.title;
      document.title = this.#t('druck.dokumenttitel', { nummer: L.formatNummer(this.#nummer) });
    }
  }

  #nachDruck() {
    if (this.#titelVorDruck !== null) {
      document.title = this.#titelVorDruck;
      this.#titelVorDruck = null;
    }
  }

  /* ----------------------------------------------------------------------------------------------
   * Zustand ändern
   * ------------------------------------------------------------------------------------------- */

  /**
   * Setzt einen neuen gültigen Zustand: prüft offene Fehler erneut, bestimmt "Individuell",
   * aktualisiert die Oberfläche und plant das Schreiben des Hash.
   */
  #setzeZustand(z, { paketPruefen = true } = {}) {
    const alt = this.#z;
    let neu = z;
    // Offene Fehler erneut prüfen (zum Beispiel Advanced, nachdem die Basis erhöht wurde)
    for (const [feldId] of [...this.#fehler]) {
      const input = this.#el.felder[feldId]?.input;
      const produkt = this.#R.felder.get(feldId)?.product;
      // Felder des nicht gewählten C2C Modells zählen nicht (03 Abschnitt 2.4): die ungültige Eingabe wird
      // verworfen, sonst sperrte ein verborgenes Feld den Warenkorb ohne sichtbare Meldung (Kandidat 18).
      // Ebenso, wenn das Produkt gerade abgewählt wurde: abgewählte Werte zählen nicht, die Fehleingabe sperrte
      // sonst den Warenkorb weiter (Befund 10).
      const abgewaehlt = Boolean(produkt && alt?.produkte?.[produkt] && !neu.produkte[produkt]);
      if (!input || abgewaehlt || !L.feldSichtbar(feldId, neu, this.#R)) {
        this.#fehler.delete(feldId);
        continue;
      }
      const r = L.pruefeMenge(input.value, feldId, neu, this.#R);
      if (r.ok) {
        this.#fehler.delete(feldId);
        neu = kopiereZustand(neu);
        neu.felder[feldId] = r.value;
        neu = L.passeAbhaengigkeitenAn(neu, this.#R).zustand;
      } else this.#fehler.set(feldId, r);
    }
    this.#z = neu;
    if (paketPruefen) this.#pruefePaket();
    this.#aktualisiere();
    this.#planeHash();
  }

  /** Paket bleibt nur, solange alle Werte seiner Empfehlung entsprechen, sonst "individuell". */
  #pruefePaket() {
    const z = this.#z;
    if (!this.#uebernommen || !z.einstieg) return;
    const soll = L.empfehle(z.einstieg, this.#uebernommen, this.#R).zustand;
    const gleich = L.konfigNummer(soll, this.#R) === L.konfigNummer(z, this.#R);
    z.paket = gleich ? this.#uebernommen : 'individuell';
  }

  #setzeFeld(feldId, wert) {
    const z = kopiereZustand(this.#z);
    z.felder[feldId] = wert;
    // Eine Menge über 0 bezieht das Produkt ein, sonst stünde 0,00 € ohne Erklärung neben dem Einzelpreis.
    // Abwählen behält die Werte und zählt sie nicht (03 Abschnitt 2.4); Sichtpruefung 12.10.2026, Befund feintuning-3.
    const fd = this.#R.felder.get(feldId);
    if (wert > 0 && fd && !z.produkte[fd.product]) z.produkte[fd.product] = true;
    this.#feldHinweise.delete(feldId);
    const a = L.passeAbhaengigkeitenAn(z, this.#R);
    for (const x of a.hinweise) {
      if (!x.feld) continue;
      this.#feldHinweise.set(x.feld, this.#t('feld.angepasst', x.params));
      this.#fehler.delete(x.feld);
    }
    this.#setzeZustand(a.zustand);
  }

  #schnappschuss() {
    return {
      z: kopiereZustand(this.#z),
      uebernommen: this.#uebernommen,
      uebernommenAntworten: this.#uebernommenAntworten,
      offen: new Set(this.#offen),
      antworten: { ...this.#antworten },
      paketeSichtbar: this.#paketeSichtbar,
    };
  }

  #stelleWiederHer(s) {
    this.#uebernommen = s.uebernommen;
    this.#uebernommenAntworten = s.uebernommenAntworten;
    this.#offen = new Set(s.offen);
    this.#antworten = { ...s.antworten };
    this.#paketeSichtbar = s.paketeSichtbar;
    this.#fehler.clear();
    this.#einstiegFehler.clear();
    this.#feldHinweise.clear();
    this.#setzeZustand(kopiereZustand(s.z), { paketPruefen: false });
  }

  #istLeer(z) {
    return L.serialisiere(z, this.#R) === L.serialisiere(L.standardZustand(this.#R), this.#R);
  }

  /* ----------------------------------------------------------------------------------------------
   * Ereignisse
   * ------------------------------------------------------------------------------------------- */

  #beiEingabe(e) {
    const t = e.target;
    if (!(t instanceof HTMLInputElement)) return;
    if (t.dataset.nrnFeld !== undefined && t.type === 'text') this.#feldEingabe(t);
    else if (t.dataset.nrnRegler !== undefined) this.#reglerEingabe(t);
    else if (t.dataset.nrnEinstieg !== undefined) this.#einstiegEingabe(t);
    else if (t.dataset.nrnEinstiegRegler !== undefined) this.#einstiegRegler(t);
  }

  #beiAenderung(e) {
    const t = e.target;
    if (!(t instanceof HTMLInputElement)) return;
    if (t.dataset.nrnFeld !== undefined) {
      if (t.type === 'checkbox' && t.checked && istGesperrt(t)) {
        // Gesperrte Position mit Fokus (aria-disabled): Einschalten wird nicht übernommen.
        t.checked = false;
        return;
      }
      if (t.type === 'checkbox') this.#setzeFeld(t.dataset.nrnFeld, t.checked ? 1 : 0);
      else this.#normalisiereAnzeige(t, this.#fehler.has(t.dataset.nrnFeld) ? null : this.#z.felder[t.dataset.nrnFeld]);
    } else if (t.dataset.nrnProduktAktiv !== undefined) {
      const id = t.dataset.nrnProduktAktiv;
      const z = kopiereZustand(this.#z);
      z.produkte[id] = t.checked;
      if (t.checked) this.#offen.add(id);
      this.#setzeZustand(z);
    } else if (t.dataset.nrnSchalter !== undefined && t.type === 'radio' && t.checked) {
      const z = kopiereZustand(this.#z);
      z.schalter[t.dataset.nrnSchalter] = t.value;
      this.#setzeZustand(z);
    } else if (t.dataset.nrnEinstieg !== undefined) {
      const id = t.dataset.nrnEinstieg;
      this.#normalisiereAnzeige(t, this.#einstiegFehler.has(id) ? null : this.#antworten[id]);
    }
  }

  /** Beim Verlassen des Felds: gültige Werte einheitlich anzeigen ("007" wird "7", "1000" wird "1.000"). */
  #normalisiereAnzeige(input, wert) {
    if (wert === null || wert === undefined) return;
    const anzeige = L.formatZahl(wert);
    if (input.value !== anzeige) input.value = anzeige;
  }

  #feldEingabe(input) {
    const id = input.dataset.nrnFeld;
    const r = L.pruefeMenge(input.value, id, this.#z, this.#R);
    this.#feldHinweise.delete(id);
    if (!r.ok) {
      this.#fehler.set(id, r);
      this.#aktualisiere();
      return;
    }
    this.#fehler.delete(id);
    this.#setzeFeld(id, r.value);
  }

  #reglerEingabe(regler) {
    const id = regler.dataset.nrnRegler;
    if (istGesperrt(regler)) {
      // aria-disabled: den Regler auf den geltenden Wert zurückstellen
      this.#aktualisiere();
      return;
    }
    const v = Number.parseInt(regler.value, 10);
    if (!Number.isSafeInteger(v) || v < 0) return;
    this.#fehler.delete(id);
    const input = this.#el.felder[id]?.input;
    if (input) input.value = L.formatZahl(v);
    this.#setzeFeld(id, v);
  }

  #schritt(id, richtung) {
    const el = this.#el.felder[id];
    if (!el || el.f.typ !== 'zahl') return;
    const z = this.#z;
    let max = el.f.max;
    if (el.f.maxOf) max = Math.min(max, z.felder[el.f.maxOf] ?? 0);
    // Steht eine Eingabe über der Grenze im Feld, zählt der angezeigte Wert, nicht der letzte gültige:
    // Minus führt dann auf die Höchstmenge (Sichtpruefung 12.10.2026, Befund feintuning-6).
    const fe = this.#fehler.get(id);
    const ueber = fe?.code === 'max' || fe?.code === 'maxOf';
    const basis = ueber ? max + 1 : (z.felder[id] ?? 0);
    const v = Math.min(Math.max(basis + richtung, 0), max);
    this.#fehler.delete(id);
    el.input.value = L.formatZahl(v);
    this.#setzeFeld(id, v);
  }

  #einstiegEingabe(input) {
    const id = input.dataset.nrnEinstieg;
    const r = L.pruefeMenge(input.value, id, this.#z, this.#R);
    if (!r.ok) {
      this.#einstiegFehler.set(id, r);
      this.#aktualisiereEinstieg();
      return;
    }
    this.#einstiegFehler.delete(id);
    this.#antworten = { ...this.#antworten, [id]: r.value };
    this.#uebernimmAntworten();
  }

  #einstiegRegler(regler) {
    const id = regler.dataset.nrnEinstiegRegler;
    const v = Number.parseInt(regler.value, 10);
    if (!Number.isSafeInteger(v)) return;
    this.#einstiegFehler.delete(id);
    this.#antworten = { ...this.#antworten, [id]: v };
    const feld = this.#el.einstieg[id]?.feld;
    if (feld) feld.value = L.formatZahl(v);
    this.#uebernimmAntworten();
  }

  /**
   * Gültige Einstiegsantworten (KO-02): Nach einer Empfehlung wirken sie sofort auf den Zustand (je
   * Arbeitsplatz, Hash e=…, Anfrage); #setzeZustand prüft dabei das Paket ("Individuell"). Vorher
   * aktualisieren sie nur Einstieg und Paketkarten.
   */
  #uebernimmAntworten() {
    if (this.#z.einstieg) {
      const z = kopiereZustand(this.#z);
      z.einstieg = { ...this.#antworten };
      this.#setzeZustand(z);
      return;
    }
    this.#aktualisiereEinstieg();
    this.#aktualisierePakete();
  }

  #beiKlick(e) {
    const ziel = e.target instanceof Element ? e.target : null;
    if (!ziel) return;
    if (ziel.closest('dialog')) return;
    const aktion = ziel.closest('[data-nrn-aktion]');
    if (aktion && this.contains(aktion)) {
      this.#aktion(aktion.dataset.nrnAktion, aktion, e);
      return;
    }
    const minus = ziel.closest('[data-nrn-minus]');
    if (minus) {
      if (!istGesperrt(minus)) this.#schritt(minus.dataset.nrnMinus, -1);
      return;
    }
    const plus = ziel.closest('[data-nrn-plus]');
    if (plus) {
      if (!istGesperrt(plus)) this.#schritt(plus.dataset.nrnPlus, 1);
      return;
    }
    const oeffnen = ziel.closest('[data-nrn-produkt-oeffnen]');
    if (oeffnen) {
      const id = oeffnen.dataset.nrnProduktOeffnen;
      if (this.#offen.has(id)) this.#offen.delete(id);
      else this.#offen.add(id);
      this.#aktualisiere();
      return;
    }
    const sprung = ziel.closest('[data-nrn-sprung]');
    if (sprung) {
      this.#setzeDetails(false);
      this.#zeigeModul(sprung.dataset.nrnSprung, true);
      return;
    }
    if (ziel.closest('[data-nrn-grenze-angebot]')) {
      this.#angebot();
      return;
    }
    if (ziel.closest('[data-nrn-hinweise-schliessen]')) {
      this.#hinweise = [];
      this.#hinweisVerwerfen = false;
      this.#aktualisiereHinweise();
      this.#el.einstieg[this.#R.einstieg[0].id]?.feld.focus({ preventScroll: true });
      return;
    }
    const meldung = ziel.closest('[data-nrn-meldung-aktion]');
    if (meldung) this.#meldungAktion(meldung.dataset.nrnMeldungAktion);
  }

  #beiTaste(e) {
    if (e.key === 'Escape' && this.#detailsOffen && this.#el.summe.bereich.contains(e.target)) {
      this.#setzeDetails(false);
      this.#el.summe.detailsKnopf.focus();
    }
  }

  #aktion(name, knopf, e) {
    switch (name) {
      case 'empfehlung': this.#empfehlung(); break;
      case 'selbst': this.#selbst(); break;
      case 'paket':
        // Die eigene Wahl gilt, auch wenn sie an einem Fehler im Einstieg scheitert: eine Vorwahl aus #paket= darf
        // später nicht still "Empfehlung anzeigen" übernehmen (Befund 21).
        this.#vorgewaehlt = null;
        this.#uebernehme(knopf.dataset.nrnPaket, false);
        break;
      case 'warenkorb': this.#warenkorb(knopf); break;
      case 'pdf': this.#drucken(); break;
      case 'teilen': this.#teilen(); break;
      case 'angebot': this.#angebot(); break;
      case 'zuruecksetzen': this.#zuruecksetzen(knopf); break;
      case 'details': this.#setzeDetails(!this.#detailsOffen); break;
      case 'verwerfen': this.#verwerfen(); break;
      case 'termin':
        if (!this.#einst.terminUrl || this.#designModus) e.preventDefault();
        break;
      default: break;
    }
  }

  #beiHashwechsel() {
    const roh = this.#hashText();
    if (roh === (this.#letzterHash ?? '')) return;
    // Andere Sprungziele (#MainContent aus "Zum Inhalt springen") ändern nichts und verdrängen keinen Hinweis (Befund 4)
    if (!roh || !L.istKonfigLink(roh)) return;
    const r = L.deserialisiere(roh, this.#R);
    const art = this.#wendeLinkAn(r, roh, false);
    if (art === 'paket') {
      // Wie beim Laden sofort übernehmen; sind die Paketkarten schon sichtbar, landet der Fokus wie bisher auf der
      // Karte, sonst auf dem Feintuning. Bei einem Fehler im Einstieg bleibt die Vorwahl für #empfehlung stehen.
      if (!this.#uebernehmeVorwahl(this.#paketeSichtbar)) this.#aktualisiere();
      return;
    }
    this.#aktualisiere();
    if (art === 'modul') {
      const id = this.#startModul;
      this.#startModul = null;
      this.#planeHash();
      this.#zeigeModulFelder(id);
    }
  }

  #beiPageshow(e) {
    if (!e.persisted) return;
    this.#beschaeftigt = false;
    if (this.#b) this.#aktualisiereKnopf();
    this.#zeigeStatus('');
    if (this.#designModus) return;
    const nummer = this.#nummer;
    fetchCart()
      .then((cart) => {
        if (L.planeWarenkorb(cart, nummer).lage === 'gleich' && this.#nummer === nummer) this.#zeigeErfolg(nummer, false);
      })
      .catch((fehler) => console.warn('[nrn konfigurator] cart refresh failed', fehler));
  }

  #beiBreite() {
    if (this.#mq?.matches) this.#setzeDetails(false);
  }

  /* ----------------------------------------------------------------------------------------------
   * Aktionen: Einstieg und Pakete
   * ------------------------------------------------------------------------------------------- */

  #ersterEinstiegFehler() {
    const id = this.#R.einstieg.map((f) => f.id).find((x) => this.#einstiegFehler.has(x));
    if (!id) return false;
    this.#el.einstieg[id].feld.focus();
    return true;
  }

  #empfehlung() {
    if (this.#ersterEinstiegFehler()) return;
    const z = kopiereZustand(this.#z);
    z.einstieg = { ...this.#antworten };
    this.#paketeSichtbar = true;
    this.#setzeZustand(z);
    // Vorwahl aus #paket=, die beim Hashwechsel an einem Fehler im Einstieg scheiterte
    if (this.#vorgewaehlt && this.#uebernehmeVorwahl(true)) return;
    this.#fokus(this.#el.paketTitel);
  }

  /**
   * Übernimmt das mit #paket= vorgemerkte Paket. Gelingt es (kein Fehler im Einstieg), ist die Vorwahl erledigt.
   * @param {boolean} karteFokussieren  Fokus auf die Paketkarte statt auf das Feintuning
   * @param {boolean} [spaeter]         Fokus und Scrollen erst vor dem nächsten Bild (Start, Layout steht dann)
   * @returns {boolean} übernommen
   */
  #uebernehmeVorwahl(karteFokussieren, spaeter = false) {
    const p = this.#vorgewaehlt;
    if (!p || !this.#uebernehme(p, karteFokussieren, spaeter)) return false;
    this.#vorgewaehlt = null;
    return true;
  }

  /** @returns {boolean} übernommen (false bei unbekanntem Paket oder Fehler im Einstieg) */
  #uebernehme(paket, karteFokussieren, spaeter = false) {
    if (!PAKETE.includes(paket)) return false;
    if (this.#ersterEinstiegFehler()) return false;
    const r = L.empfehle(this.#antworten, paket, this.#R);
    // Ein Paket ist übernommen: eine ältere Vorwahl aus #paket= ist erledigt (Befund 21).
    this.#vorgewaehlt = null;
    this.#uebernommen = paket;
    this.#uebernommenAntworten = this.#antwortSchluessel(r.zustand.einstieg);
    this.#paketeSichtbar = true;
    this.#fehler.clear();
    this.#feldHinweise.clear();
    this.#hinweise = r.hinweise;
    this.#hinweisVerwerfen = false;
    const erstes = this.#R.produktReihenfolge.find((id) => r.zustand.produkte[id]);
    this.#offen = new Set(erstes ? [erstes] : []);
    this.#setzeZustand(r.zustand, { paketPruefen: false });
    this.#zeigeStatus(this.#t('pakete.uebernommen', { paket: this.#paketName(paket) }), { dauer: STATUS_DAUER });
    const ziel = karteFokussieren ? this.#el.pakete[paket].karte : this.#el.feintuningTitel;
    if (spaeter) naechstesBild(() => this.#fokus(ziel));
    else this.#fokus(ziel);
    return true;
  }

  #selbst() {
    const vorher = this.#schnappschuss();
    const warLeer = this.#istLeer(vorher.z);
    this.#uebernommen = null;
    this.#uebernommenAntworten = null;
    this.#vorgewaehlt = null;
    this.#fehler.clear();
    this.#feldHinweise.clear();
    this.#offen = new Set();
    this.#setzeZustand(L.standardZustand(this.#R), { paketPruefen: false });
    if (!warLeer) this.#bieteRueckgaengig(vorher);
    this.#fokus(this.#el.feintuningTitel);
  }

  async #zuruecksetzen(knopf) {
    const antwort = await this.#frage('zuruecksetzen', knopf);
    if (antwort !== 'bestaetigen') return;
    const vorher = this.#schnappschuss();
    const z = this.#z.einstieg && this.#uebernommen
      ? L.empfehle(this.#z.einstieg, this.#uebernommen, this.#R).zustand
      : L.standardZustand(this.#R);
    if (!(this.#z.einstieg && this.#uebernommen)) this.#uebernommen = null;
    // Zurück auf das Paket mit den jetzigen Antworten: der Hinweis auf geänderte Antworten entfällt.
    this.#uebernommenAntworten = this.#uebernommen ? this.#antwortSchluessel(this.#z.einstieg) : null;
    this.#fehler.clear();
    this.#feldHinweise.clear();
    this.#setzeZustand(z, { paketPruefen: false });
    this.#bieteRueckgaengig(vorher);
  }

  #bieteRueckgaengig(vorher) {
    if (this.#rueckgaengig?.timer) clearTimeout(this.#rueckgaengig.timer);
    const timer = setTimeout(() => {
      if (this.#rueckgaengig?.timer === timer) {
        this.#rueckgaengig = null;
        this.#zeigeStatus('');
      }
    }, RUECKGAENGIG_DAUER);
    this.#rueckgaengig = { vorher, timer };
    this.#zeigeStatus(this.#t('status.zurueckgesetzt'), {
      knopf: { text: this.#t('aktion.rueckgaengig'), aktion: 'rueckgaengig' },
    });
  }

  #verwerfen() {
    speicherSchreiben('localStorage', SPEICHER_LETZTE, null);
    this.#hinweise = [];
    this.#hinweisVerwerfen = false;
    this.#uebernommen = null;
    this.#uebernommenAntworten = null;
    this.#vorgewaehlt = null;
    this.#paketeSichtbar = false;
    this.#offen = new Set();
    this.#antworten = this.#standardAntworten();
    this.#fehler.clear();
    this.#einstiegFehler.clear();
    this.#feldHinweise.clear();
    this.#z = L.standardZustand(this.#R);
    // Kam die Seite mit #modul=, gilt das Verwerfen nur der wiederhergestellten Konfiguration (Befund 7).
    const modul = this.#linkModul;
    this.#linkModul = null;
    if (modul && this.#R.produkte.has(modul)) {
      this.#z.produkte[modul] = true;
      this.#offen.add(modul);
    }
    clearTimeout(this.#timer.hash);
    this.#timer.hash = 0;
    this.#letzterHash = '';
    if (!this.#designModus) {
      try {
        history.replaceState(history.state, '', `${location.pathname}${location.search}`);
      } catch {
        /* ohne Verlauf weiter */
      }
    }
    this.#aktualisiere();
    // Mit Modul aus dem Link ist die Konfiguration nicht leer: Link und Speicher zeigen dann sie.
    if (modul && this.#z.produkte[modul]) this.#planeHash();
    this.#el.einstieg[this.#R.einstieg[0].id]?.feld.focus();
  }

  #zeigeModul(id, fokussieren) {
    const el = this.#el.produkte[id];
    if (!el) return;
    if (!this.#offen.has(id)) {
      this.#offen.add(id);
      this.#aktualisiere();
    }
    try {
      el.modul.scrollIntoView({ behavior: ruhig() ? 'auto' : 'smooth', block: 'start' });
    } catch {
      /* ältere Browser */
    }
    if (fokussieren) el.oeffnen.focus({ preventScroll: true });
  }

  /**
   * Sprung aus #modul=: Abschnitt offen, Ansicht auf die Mengenfelder, Fokus auf das erste bedienbare Mengenfeld.
   * Vorher zeigte der Sprung nur den Kopf mit Haken bei 0,00 €, ohne dass klar war, was zu tun ist (QA 15.10.2026).
   * Mengen bleiben, wie sie sind; nichts wird vorbelegt. Ohne bedienbares Feld (alles gesperrt) bleibt es beim Kopf.
   */
  #zeigeModulFelder(id) {
    const el = this.#el.produkte[id];
    if (!el) return;
    if (!this.#offen.has(id)) {
      this.#offen.add(id);
      this.#aktualisiere();
    }
    const feld = this.#mengenfelder(id)
      .find((x) => !x.input.disabled && x.input.getAttribute('aria-disabled') !== 'true');
    if (!feld) {
      this.#zeigeModul(id, false);
      return;
    }
    feld.input.focus({ preventScroll: true });
    try {
      feld.zeile.scrollIntoView({ behavior: ruhig() ? 'auto' : 'smooth', block: 'start' });
    } catch {
      /* ältere Browser */
    }
  }

  /** Sichtbare Mengenfelder (Zahl) eines Produkts in der Reihenfolge der Regeldatei. */
  #mengenfelder(id) {
    return [...this.#R.felder.values()]
      .filter((f) => f.product === id && f.typ === 'zahl')
      .map((f) => this.#el.felder[f.id])
      .filter((x) => x?.input && !x.zeile.closest('[hidden]'));
  }

  #fokus(el) {
    if (!el) return;
    el.focus({ preventScroll: true });
    try {
      el.scrollIntoView({ behavior: ruhig() ? 'auto' : 'smooth', block: 'start' });
    } catch {
      /* ältere Browser */
    }
  }

  #setzeDetails(offen) {
    this.#detailsOffen = Boolean(offen);
    const s = this.#el.summe;
    setzeAttr(s.bereich, 'data-nrn-offen', this.#detailsOffen ? 'true' : null);
    setzeAttr(s.detailsKnopf, 'aria-expanded', this.#detailsOffen ? 'true' : 'false');
    setzeText(s.detailsText, this.#t(this.#detailsOffen ? 'summe.detailsSchliessen' : 'summe.details'));
    // E11 Variante B: der Sperrgrund steht als letztes Kind im Detailblatt, direkt ueber dem Knopf. Auf dem Handy
    // scrollt das Blatt (max-height), darum beim Oeffnen ans Ende, damit der Grund ohne Wischen sichtbar ist.
    // Auf dem Desktop ist das Blatt kein Scrollbereich, die Zuweisung bleibt dort wirkungslos.
    if (this.#detailsOffen && !s.sperrgrund.hidden) s.details.scrollTop = s.details.scrollHeight;
  }

  /* ----------------------------------------------------------------------------------------------
   * Hash und Speicher
   * ------------------------------------------------------------------------------------------- */

  #planeHash() {
    if (this.#designModus) return;
    clearTimeout(this.#timer.hash);
    this.#timer.hash = setTimeout(() => this.#schreibeHash(), HASH_VERZOEGERUNG);
  }

  #schreibeHashJetzt() {
    if (this.#timer.hash) this.#schreibeHash();
  }

  #schreibeHash() {
    clearTimeout(this.#timer.hash);
    this.#timer.hash = 0;
    if (this.#designModus || !this.#z) return;
    const hash = L.serialisiere(this.#z, this.#R);
    this.#letzterHash = hash;
    try {
      if (this.#hashText() !== hash) history.replaceState(history.state, '', `${location.pathname}${location.search}#${hash}`);
    } catch {
      /* ohne Verlauf weiter */
    }
    speicherSchreiben('localStorage', SPEICHER_LETZTE, hash);
  }

  /* ----------------------------------------------------------------------------------------------
   * Meldungen
   * ------------------------------------------------------------------------------------------- */

  /**
   * Statusbereich (role=status). opts.knopf { text, aktion } oder opts.link { text, href }; opts.dauer in ms.
   * @returns {HTMLElement|null} Knopf bzw. Link der Meldung
   */
  #zeigeStatus(text, opts = {}) {
    const s = this.#el.summe.status;
    clearTimeout(this.#timer.status);
    this.#erfolgNummer = null;
    if (!text) {
      s.replaceChildren();
      return null;
    }
    let extra = null;
    if (opts.knopf) {
      extra = h('button', { type: 'button', class: ['k-link', 'nrn-konf__textknopf'], 'data-nrn-meldung-aktion': opts.knopf.aktion },
        opts.knopf.text);
    } else if (opts.link) {
      extra = h('a', { class: 'k-link', href: opts.link.href, 'data-nrn-zum-warenkorb': '' }, opts.link.text);
    }
    fuelle(s, h('span', {}, text), extra ? ' ' : null, extra);
    if (opts.dauer) this.#timer.status = setTimeout(() => s.replaceChildren(), opts.dauer);
    return extra;
  }

  #zeigeFehler(text, zusatz, knopf) {
    const f = this.#el.summe.fehler;
    this.#fehlerAlteOffen = Boolean(text) && knopf?.aktion === 'alteEntfernen';
    if (!text) {
      f.replaceChildren();
      setzeVerborgen(f, true);
      return;
    }
    fuelle(f,
      h('p', { class: 'k-error' }, symbol('achtung'), h('span', {}, text)),
      zusatz ? h('p', { class: 'nrn-konf__fehler-zusatz' }, zusatz) : null,
      knopf ? h('button', {
        type: 'button', class: ['k-btn', 'k-btn--outline', 'k-btn--sm'], 'data-nrn-meldung-aktion': knopf.aktion,
      }, knopf.text) : null);
    setzeVerborgen(f, false);
    f.focus({ preventScroll: true });
  }

  #meldungAktion(aktion) {
    if (aktion === 'rueckgaengig' && this.#rueckgaengig) {
      const { vorher, timer } = this.#rueckgaengig;
      clearTimeout(timer);
      this.#rueckgaengig = null;
      this.#zeigeStatus('');
      this.#stelleWiederHer(vorher);
      this.#el.abschluss.zuruecksetzen.focus({ preventScroll: true });
    } else if (aktion === 'neuLaden') {
      window.location.reload();
    } else if (aktion === 'alteEntfernen') {
      this.#alteEntfernen();
    }
  }

  #zeigeSperrgrund() {
    // "Bitte prüfen Sie die markierten Eingaben": zur ersten Fehleingabe, auch wenn ihr Modul zugeklappt ist; vorher
    // zeigte der Sperrgrund auf ein unsichtbares Feld (Befund 10).
    if (this.#b?.sperrgrund === 'eingabe' && this.#zeigeErstenFeldFehler()) return;
    const s = this.#el.summe;
    if (!this.#mq?.matches) this.#setzeDetails(true);
    if (!s.sperrgrund.hidden) s.sperrgrund.focus({ preventScroll: false });
  }

  /**
   * Öffnet das Modul der ersten Fehleingabe (Reihenfolge der Regeldatei) und setzt den Fokus in das Feld.
   * @returns {boolean} ein Feld hat den Fokus
   */
  #zeigeErstenFeldFehler() {
    const feldId = [...this.#R.felder.keys()].find((id) => this.#fehler.has(id));
    const el = feldId ? this.#el.felder[feldId] : null;
    if (!el?.input) return false;
    const produkt = el.f.product;
    if (!this.#offen.has(produkt)) {
      this.#offen.add(produkt);
      this.#aktualisiere();
    }
    if (el.zeile.closest('[hidden]')) return false;
    // Das Summenblatt läge auf dem Handy über dem Feld
    if (this.#detailsOffen) this.#setzeDetails(false);
    el.input.focus({ preventScroll: true });
    try {
      el.zeile.scrollIntoView({ behavior: ruhig() ? 'auto' : 'smooth', block: 'center' });
    } catch {
      /* ältere Browser */
    }
    return true;
  }

  #drawerOffen() {
    const d = document.querySelector('theme-drawer#cart-drawer');
    if (!d) return false;
    return d.isOpen === true || d.hasAttribute('open') || Boolean(d.querySelector?.('dialog[open]'));
  }

  #zeigeErfolg(nummer, fokussieren) {
    const link = this.#zeigeStatus(this.#t('warenkorb.erfolg', { nummer: L.formatNummer(nummer) }), {
      link: { text: this.#t('aktion.zumWarenkorb'), href: this.#warenkorbUrl() },
    });
    this.#erfolgNummer = nummer;
    if (!fokussieren || !link) return;
    clearTimeout(this.#timer.fokus);
    this.#timer.fokus = setTimeout(() => {
      if (!this.#drawerOffen() && link.isConnected) link.focus({ preventScroll: true });
    }, 350);
  }

  #setzeBeschaeftigt(an) {
    this.#beschaeftigt = Boolean(an);
    this.#aktualisiereKnopf();
  }

  /* ----------------------------------------------------------------------------------------------
   * Dialoge
   * ------------------------------------------------------------------------------------------- */

  /**
   * Öffnet einen nativen modalen Dialog und liefert die gewählte Aktion
   * (ersetzen, zusaetzlich, abbrechen, zum-warenkorb, bestaetigen). Escape bricht ab.
   * textSchluessel wählt den Dialogtext mit params (Standard beim Ersetzen: dialog.ersetzen.text).
   */
  #frage(name, ausloeser, params, textSchluessel) {
    const d = this.#el.dialoge[name];
    const schluessel = textSchluessel ?? (name === 'ersetzen' ? 'dialog.ersetzen.text' : null);
    if (schluessel) setzeText(d.text, this.#t(schluessel, params));
    return new Promise((resolve) => {
      let fertig = false;
      const ende = (aktion) => {
        if (fertig) return;
        fertig = true;
        d.dialog.removeEventListener('click', klick);
        d.dialog.removeEventListener('cancel', abbruch);
        d.dialog.removeEventListener('close', geschlossen);
        if (d.dialog.open) d.dialog.close();
        if (aktion !== 'zum-warenkorb' && ausloeser?.isConnected) ausloeser.focus({ preventScroll: true });
        resolve(aktion);
      };
      const klick = (e) => {
        const k = e.target instanceof Element ? e.target.closest('[data-nrn-dialog-aktion]') : null;
        if (!k || !d.dialog.contains(k)) return;
        if (k.tagName !== 'A') e.preventDefault();
        ende(k.dataset.nrnDialogAktion);
      };
      const abbruch = (e) => {
        e.preventDefault();
        ende('abbrechen');
      };
      const geschlossen = () => ende('abbrechen');
      d.dialog.addEventListener('click', klick);
      d.dialog.addEventListener('cancel', abbruch);
      d.dialog.addEventListener('close', geschlossen);
      try {
        if (typeof d.dialog.showModal === 'function') d.dialog.showModal();
        else d.dialog.setAttribute('open', '');
      } catch {
        d.dialog.setAttribute('open', '');
      }
      d.start?.focus();
    });
  }

  /* ----------------------------------------------------------------------------------------------
   * Warenkorb (03 Abschnitt 5)
   * ------------------------------------------------------------------------------------------- */

  async #warenkorb(knopf) {
    if (this.#beschaeftigt) return;
    this.#schreibeHashJetzt();
    const b = this.#b;
    if (!b.warenkorbErlaubt) {
      this.#zeigeSperrgrund();
      return;
    }
    if (this.#designModus) return;

    const R = this.#R;
    const z = kopiereZustand(this.#z);
    const nummer = L.konfigNummer(z, R);
    const hash = L.serialisiere(z, R);
    this.#zeigeFehler('');
    this.#setzeBeschaeftigt(true);
    this.#zeigeStatus(this.#t('warenkorb.laeuft'));
    try {
      const cart = await fetchCart();
      const plan = L.planeWarenkorb(cart, nummer);
      let alteNummern = [];
      let ersetzen = false;
      if (plan.lage === 'gleich') {
        this.#zeigeStatus('');
        await this.#frage('gleich', knopf);
        return;
      }
      if (plan.lage === 'andere') {
        this.#zeigeStatus('');
        const antwort = await this.#frage('ersetzen', knopf, {
          nummer: plan.alteNummern.map((nr) => L.formatNummer(nr)).join(', '),
          zeilen: L.formatZahl(plan.alteZeilenAnzahl),
          betrag: L.formatCent(plan.alteSummeCent),
        }, plan.alteZeilenAnzahl === 1 ? 'dialog.ersetzen.text.eins' : 'dialog.ersetzen.text');
        if (antwort !== 'ersetzen' && antwort !== 'zusaetzlich') return;
        ersetzen = antwort === 'ersetzen';
        alteNummern = plan.alteNummern;
        this.#zeigeStatus(this.#t('warenkorb.laeuft'));
      }

      const payload = L.warenkorbPayload(b.zeilen, {
        nummer, hash, stand: R.stand,
        abrechnungKurz: b.abrechnung.art === 'erstmonat' ? (this.#einst.abrechnungKurz || null) : null,
      });
      const lines = payload.items.map((i) => ({
        variantId: i.id, quantity: i.quantity, properties: i.properties, sellingPlanId: i.selling_plan ?? null,
      }));
      const ergebnis = ersetzen
        ? await replaceConfiguration(alteNummern, lines, { source: this })
        : await addLinesToCart(lines, { source: this });

      if (!L.pruefeWarenkorbAbgleich(ergebnis.cart, nummer, b.nettoCent)) {
        this.#zeigeStatus('');
        this.#zeigeFehler(this.#t('warenkorb.preisAbweichung'), null, { text: this.#t('aktion.neuLaden'), aktion: 'neuLaden' });
        return;
      }
      if (ersetzen && ergebnis.alteEntfernt === false) {
        this.#offeneAlte = alteNummern;
        this.#offeneNummer = nummer;
        this.#zeigeErfolg(nummer, false);
        this.#zeigeFehler(this.#t('warenkorb.altNichtEntfernt'), null, { text: this.#t('aktion.erneut'), aktion: 'alteEntfernen' });
        return;
      }
      this.#zeigeErfolg(nummer, true);
    } catch (fehler) {
      closeCartDrawer();
      console.warn('[nrn konfigurator] cart update failed', fehler);
      const antwort = fehler instanceof NrnCartError ? fehler.response : null;
      const beschreibung = fehler instanceof NrnCartError && fehler.status >= 400 && typeof antwort?.description === 'string'
        ? antwort.description.trim() : '';
      this.#zeigeStatus('');
      this.#zeigeFehler(this.#t('warenkorb.fehler'), beschreibung || null, null);
    } finally {
      this.#setzeBeschaeftigt(false);
    }
  }

  async #alteEntfernen() {
    if (this.#beschaeftigt || this.#designModus) return;
    this.#setzeBeschaeftigt(true);
    try {
      for (const nr of this.#offeneAlte) await removeConfiguration(nr, { source: this });
      // Im Warenkorb liegt die Konfiguration vom Ersetzen, nicht die inzwischen geänderte (Befund 20).
      const nummer = this.#offeneNummer ?? this.#nummer;
      this.#offeneAlte = [];
      this.#offeneNummer = null;
      this.#zeigeFehler('');
      this.#zeigeErfolg(nummer, true);
    } catch (fehler) {
      console.warn('[nrn konfigurator] removing old configuration failed', fehler);
      this.#zeigeFehler(this.#t('warenkorb.altNichtEntfernt'), null, { text: this.#t('aktion.erneut'), aktion: 'alteEntfernen' });
    } finally {
      this.#setzeBeschaeftigt(false);
    }
  }

  /* ----------------------------------------------------------------------------------------------
   * Druck, Teilen, Angebot
   * ------------------------------------------------------------------------------------------- */

  #drucken() {
    this.#vorDruck();
    try {
      window.print();
    } finally {
      // Browser, die print() blockierend ausführen, setzen den Titel hier zurück; sonst afterprint.
      setTimeout(() => this.#nachDruck(), 0);
    }
  }

  async #teilen() {
    this.#schreibeHashJetzt();
    const url = this.#linkUrl(L.serialisiere(this.#z, this.#R));
    const a = this.#el.abschluss;
    setzeVerborgen(a.linkfeld, true);
    // Der Knopf verspricht Kopieren: mit Maus zuerst die Zwischenablage mit Rückmeldung, das Teilen Fenster nur auf
    // Touch Geräten (grober Zeiger), dort ersetzt es das Kopieren (Sichtpruefung 12.10.2026, Befund abschluss-5).
    if (istTouchGeraet() && typeof navigator.share === 'function') {
      try {
        await navigator.share({ url, title: this.#t('druck.dokumenttitel', { nummer: L.formatNummer(this.#nummer) }) });
        return;
      } catch (fehler) {
        if (fehler?.name === 'AbortError') return;
      }
    }
    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard unavailable');
      await navigator.clipboard.writeText(url);
      this.#zeigeStatus(this.#t('status.linkKopiert'), { dauer: STATUS_DAUER });
    } catch {
      a.linkfeld.value = url;
      setzeVerborgen(a.linkfeld, false);
      a.linkfeld.focus();
      a.linkfeld.select();
      this.#zeigeStatus(this.#t('status.linkManuell'), { dauer: STATUS_DAUER });
    }
  }

  #angebot() {
    if (this.#designModus) return;
    this.#schreibeHashJetzt();
    const R = this.#R;
    const z = this.#z;
    const b = this.#b;
    const url = this.#linkUrl(L.serialisiere(z, R));
    let paketName = null;
    if (PAKETE.includes(z.paket)) paketName = this.#paketName(z.paket);
    else if (z.paket === 'individuell') paketName = this.#t('pakete.individuell');
    let abrechnungHinweis = null;
    if (b.abrechnung.art === 'erstmonat' && b.zeilen.length) abrechnungHinweis = this.#einst.hinweisAbrechnung || null;
    else if (b.abrechnung.art === 'abo' && b.abrechnung.planNamen.length) {
      abrechnungHinweis = this.#t('summe.abrechnungPlan', { plan: b.abrechnung.planNamen.join(', ') });
    }
    // E14 und E15 gehoeren auch in den Anfragetext. Stand 08.10.2026: anfrageText (nrn-konfigurator-logik.js, Hauptsitzung)
    // liest einrichtungText und laufzeitText noch NICHT; die Felder werden hier schon uebergeben, damit die Auswertung dort
    // ohne Aenderung an dieser Datei nachgezogen werden kann (Audit Befund, offen bei der Hauptsitzung).
    const text = L.anfrageText(z, b, R, this.#texte, {
      url, paketName, abrechnungHinweis,
      einrichtungText: this.#einst.einrichtungText || null,
      laufzeitText: this.#einst.laufzeitText || null,
    });
    const pfad = this.#einst.kontaktPfad || STANDARD_KONTAKT;
    // sessionStorage gilt nur für diese Herkunft: zeigt kontakt_pfad auf eine andere, käme die Anfrage dort leer an
    // (Befund 25). Dann immer der Kurztext über ?anfrage=.
    if (!fremdeHerkunft(pfad) && speicherSchreiben('sessionStorage', SPEICHER_ANFRAGE, text)) {
      window.location.href = pfad;
      return;
    }
    const ap = z.einstieg?.ap > 0 ? z.einstieg.ap
      : (z.produkte.managedRmm && z.felder['managedRmm.workstations'] > 0 ? z.felder['managedRmm.workstations'] : 0);
    const kurz = this.#t('anfrage.kurz', { nummer: L.formatNummer(this.#nummer), ap: String(ap), url });
    window.location.href = mitAnfrage(pfad, kurz);
  }
}

if (!customElements.get('nrn-konfigurator')) customElements.define('nrn-konfigurator', NrnKonfigurator);

export { NrnKonfigurator, reglerFortschritt };
